import {
    antBlue,
    antGold,
    antGreen,
    antGrey,
    BUILD_BUCKET_COLORS,
    runwayColors,
} from '@/components/styles/globalStyles';
import {
    AllAndroidRolloutStartStrategies,
    AndroidAutoRolloutStartStrategy,
    AndroidOutputType,
    AutoBackmergeStrategy,
    AppBetaBuildInfo,
    AppEntity,
    AppleAppStorePhasedReleaseState,
    AppleAppStoreReleaseType,
    AppleCertificateType,
    AppleExternalBetaBuildState,
    AppleInternalBetaBuildState,
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppMonitoringHealthMetricType,
    AppMonitoringObservabilityAndAnalyticsHealthMetric,
    AppNotificationCategory,
    AppNotificationType,
    AppPlatform,
    AppStoreArtifactUploadRule,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppStoreReviewSubmissionState,
    AppStoreType,
    ASCRegistrationStatus,
    ASCRole,
    AutoCherryPickStrategy,
    AutomationCategory,
    AutomationReleaseTypes,
    AutomationStatus,
    AutomationType,
    AutoReleaseStrategy,
    AutoSubmitStrategy,
    AutoSyncTranslationsExportStrategy,
    AzureProviderData,
    BetaSoakStatus,
    BetaStatus,
    BitriseProviderData,
    BucketType,
    BuildArtifactType,
    BuildDistroBucketRuleType,
    BuildDistroMemberPermissionLevel,
    BuildDistroNotificationType,
    BuildInfo,
    BuildkiteProviderData,
    BuildNotesAutomationStrategy,
    BuildStatus,
    BumpVersionAutomationStrategy,
    CadenceTargetDateSet,
    ChecklistItem,
    ChecklistItemPlacement,
    ChecklistItemStatusType,
    CodemagicProviderData,
    CustomMessageConfigKey,
    DayOfTheWeek,
    DatadogCustomMetric,
    DefaultUserGroupId,
    DeviceClass,
    EmailNotificationType,
    EventAndParam,
    ExpoEASRolloutState,
    FeatureFlagStatus,
    FeatureItemCodeStatus,
    ForgotPasswordError,
    GitHubProviderData,
    GoogleBetaBuildDetail,
    GooglePlayConsolePermission,
    GooglePlayStoreState,
    HuaweiPhasedReleaseState,
    InAppPurchaseType,
    Integration,
    IntegrationCapability,
    IntegrationProvider,
    IntegrationType,
    IssueSourceType,
    IssueState,
    LifecycleEventType,
    LifecycleFreezeSet,
    LocalizationStatus,
    MetadataTranslationStatus,
    MonitoringAutomationConditionType,
    NotificationTriggerConditionType,
    MonitoringMetricHealthStatus,
    ObservabilityAndAnalyticsMetricType,
    OrgUser,
    OverviewCodeContributor,
    PhasedReleaseState,
    ProvisioningProfileType,
    PullRequestType,
    RelativeTiming,
    ReleaseCalendarEventType,
    ReleaseMethod,
    ReleaseNotesDataSource,
    ReleaseScheduleCadenceSettings,
    ReleaseScheduleCadenceStyle,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepType,
    ReleaseTimelinePhase,
    ReleaseType,
    ReleaseTypesForNonBlockingSteps,
    RetainedBuildsStrategy,
    RollbackResigningStrategy,
    Screenshot,
    SettingType,
    StabilityMetricType,
    StatisticUnit,
    SubscriptionStatus,
    TimelineEventType,
    TransactionType,
    UserGroup,
    UserGroupId,
    UserPermissions,
    UserRatingsMetricType,
    BuildDetails,
    BuildDistroArtifactDownloadStatus,
    PullRequestMergeQueueState,
} from '@/types';
import { capitalize, isNil, last } from 'lodash';
import { dayjs } from '@/extendedDayjs';
import {
    capitalizeFirstLetter,
    displayNameForCapability,
    displayStatValue,
    displayStatValueWithSign,
    getIsApplePlatform,
    getIsGooglePlayStore,
    getActiveScheduleTimezone,
    isGooglePlatform,
    isIosPlatform,
    isMultipleReleasesCadence,
    isPercentageMonitoringMetric,
    statTypeForMonitoringMetricType,
    unitForMonitoringMetricType,
} from '@/helpers';
import { DatetimeFormat } from '@/timeHelpers';
import { ApprovalsSortType, AutomationsToolboxSortType, FeatureFlagsSortType, SortType } from '@/types/sorts';
import {
    MonitoringStatisticGroup,
    SetupStepsCallerContext,
    SetupStepType,
    symbolForMetricConditionOperator,
} from '@/typescriptHelpers';
import {
    AppstoreAddOutlined,
    AppstoreOutlined,
    BarChartOutlined,
    BarsOutlined,
    BellOutlined,
    BugOutlined,
    CalendarOutlined,
    CarryOutOutlined,
    CheckCircleFilled,
    CheckCircleOutlined,
    CheckOutlined,
    ClearOutlined,
    ClockCircleFilled,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    CloudDownloadOutlined,
    CloudUploadOutlined,
    CodeOutlined,
    CommentOutlined,
    DeleteRowOutlined,
    DiffOutlined,
    DownloadOutlined,
    EditOutlined,
    FieldBinaryOutlined,
    FileTextOutlined,
    FileZipOutlined,
    FlagOutlined,
    InboxOutlined,
    KeyOutlined,
    LogoutOutlined,
    MailOutlined,
    MessageOutlined,
    MinusCircleOutlined,
    NotificationOutlined,
    PaperClipOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    ProfileOutlined,
    PullRequestOutlined,
    QrcodeOutlined,
    ReconciliationOutlined,
    RetweetOutlined,
    RiseOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SearchOutlined,
    ShakeOutlined,
    StarOutlined,
    StopOutlined,
    SyncOutlined,
    TagOutlined,
    TagsOutlined,
    TeamOutlined,
    ThunderboltFilled,
    ToolOutlined,
    ToTopOutlined,
    TranslationOutlined,
    UndoOutlined,
    UploadOutlined,
    UsergroupAddOutlined,
    UserOutlined,
    UserSwitchOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { datadogCustomMetricDisplayName } from './datadogCustomMetrics';
import { OverviewDestinationStatus, WorkflowRunOverviewDisplayStatus } from '@/types/workflowOverview';
import { TimezonePreferenceEnum } from '@/components/userSettings/EditTimezoneSettingsModal';

export const displayTextForReleaseStepStatus: (status: ReleaseStepStatus) => string = (status) => {
    switch (status) {
        case ReleaseStepStatus.Blocked:
            return 'Blocked';
        case ReleaseStepStatus.Inactive:
            return 'Inactive';
        case ReleaseStepStatus.Pending:
            return 'Pending';
        case ReleaseStepStatus.PendingChecklistOnly:
            return 'Pending checklist';
        case ReleaseStepStatus.Ready:
            return 'Ready';
        case ReleaseStepStatus.Skipped:
            return 'Skipped';
        default:
            return 'Unknown';
    }
};

export const displayTextForIssueSourceType: (type: IssueSourceType) => string = (type) => {
    switch (type) {
        case IssueSourceType.Runway:
            return 'Team';
        case IssueSourceType.PMTicket:
            return 'Tickets';
        case IssueSourceType.UserReviews:
            return 'User reviews';
        case IssueSourceType.BetaFeedback:
            return 'Beta feedback';
        case IssueSourceType.RegressionTesting:
            return 'Regression testing';
        case IssueSourceType.StabMon:
            return 'Stability monitoring';
        case IssueSourceType.OBAA:
            return 'Observability & analytics';
    }
};

export const displayTextForIssueState = (state: IssueState): string => {
    switch (state) {
        case IssueState.New:
            return 'Needs triage';
        case IssueState.Assigned:
            return 'Assigned';
        case IssueState.FixRequested:
            return 'Fix requested';
        case IssueState.FixApproved:
            return 'Fix approved';
        case IssueState.Reopened:
            return 'Reopened';
        case IssueState.FixMerged:
            return 'Resolved';
        case IssueState.FixRejected:
            return 'Fix rejected';
        case IssueState.NotAPriority:
            return 'Not a priority';
        case IssueState.ResolvedExternally:
            return 'Resolved externally';
    }
};

export const metadataRequirementsLinkForPlatform: (platform: AppPlatform) => string | null = (
    platform: AppPlatform,
) => {
    switch (platform) {
        case AppPlatform.Ios:
            return 'https://help.apple.com/asc/appsspec/en.lproj/static.html';
        case AppPlatform.Android:
            return 'https://developer.android.com/distribute/best-practices/launch/store-listing';
        case AppPlatform.TvOS:
            return 'https://help.apple.com/asc/appsspec/en.lproj/static.html';
        case AppPlatform.VisionOS:
            return 'https://help.apple.com/asc/appsspec/en.lproj/static.html';
        case AppPlatform.WearOS:
            return 'https://support.google.com/googleplay/android-developer/answer/13295490?hl=en';
        default:
            return null;
    }
};

export const displayVersionReleaseType = (releaseType?: AppleAppStoreReleaseType, scheduledDate?: string) => {
    switch (releaseType) {
        case AppleAppStoreReleaseType.Manual:
            return 'Manually release this version';
        case AppleAppStoreReleaseType.Scheduled:
            return `Release no earlier than ${dayjs(scheduledDate).format(
                DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated,
            )}`;
        case AppleAppStoreReleaseType.AfterApproval:
            return 'Automatically release this version when approved';
        default:
            return 'Select version release type';
    }
};

export const displayVersionReleaseTypeSetting = (releaseType: AppleAppStoreReleaseType) => {
    switch (releaseType) {
        case AppleAppStoreReleaseType.Manual:
            return 'Manually release this version';
        case AppleAppStoreReleaseType.Scheduled:
            return `Automatically release after App Review, no earlier than:`;
        case AppleAppStoreReleaseType.AfterApproval:
            return 'Automatically release this version';
    }
};

export const groupPhaseTitleForReleasePhase = (releasePhase: ReleaseTimelinePhase) => {
    switch (releasePhase) {
        case ReleaseTimelinePhase.Upcoming:
            return 'Upcoming';
        case ReleaseTimelinePhase.Current:
            return 'Next release';
        case ReleaseTimelinePhase.Completed:
            return 'Completed';
        case ReleaseTimelinePhase.Rollback:
            return 'Rollback';
    }
};

export const displayTextForAppPlatform = (platform: AppPlatform, appStoreType?: AppStoreType) => {
    switch (platform) {
        case AppPlatform.Ios:
            return 'iOS';
        case AppPlatform.Android:
            if (appStoreType) {
                switch (appStoreType) {
                    case AppStoreType.Amazon:
                        return 'Android – Amazon Appstore';
                    case AppStoreType.Huawei:
                        return 'Android – Huawei AppGallery';
                    case AppStoreType.Samsung:
                        return 'Android – Samsung Galaxy Store';
                    default:
                        return 'Android';
                }
            }
            return 'Android';
        case AppPlatform.AndroidTV:
            return 'Android TV';
        case AppPlatform.WearOS:
            return 'Wear OS';
        case AppPlatform.AndroidSDK:
            return 'Android SDK';
        case AppPlatform.IosSDK:
            return 'iOS SDK';
        case AppPlatform.ReactNativeOTA:
            return 'React Native';
        case AppPlatform.ExpoEAS:
            return 'Expo EAS';
        case AppPlatform.TvOS:
            return 'tvOS';
        case AppPlatform.VisionOS:
            return 'visionOS';
        case AppPlatform.WatchOS:
            return 'watchOS';
        case AppPlatform.Samsung:
            return 'Samsung Galaxy Store';
        case AppPlatform.Huawei:
            return 'Huawei AppGallery';
        case AppPlatform.Amazon:
            return 'Amazon Appstore';
    }
};

export const displayCompanyForPlatform = (platform: AppPlatform, appStoreType?: AppStoreType) => {
    switch (platform) {
        case AppPlatform.TvOS:
        case AppPlatform.Ios:
        case AppPlatform.IosSDK:
            return 'Apple';
        case AppPlatform.AndroidSDK:
            return 'Google';
        case AppPlatform.WearOS:
        case AppPlatform.Android:
            switch (appStoreType) {
                case AppStoreType.Amazon:
                    return 'Amazon';
                case AppStoreType.Huawei:
                    return 'Huawei';
                case AppStoreType.Samsung:
                    return 'Samsung';
                default:
                    return 'Google';
            }
        default:
            return null;
    }
};

export const displayBuildTypeForPlatformAndAppStoreType = (platform: AppPlatform, appStoreType: AppStoreType) => {
    if (getIsApplePlatform(platform)) {
        return 'build';
    } else if (isGooglePlatform(platform)) {
        switch (appStoreType) {
            case AppStoreType.Google:
            case AppStoreType.Huawei:
            case AppStoreType.Samsung:
                return 'App Bundle / APK';
            case AppStoreType.Amazon:
                return 'APK';
            default:
                return null;
        }
    }
    return null;
};

export const displayConsoleNameForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'App Store Connect';
        case AppStoreType.Google:
            return 'Play Console';
        case AppStoreType.Amazon:
            return 'Amazon Developer Portal';
        case AppStoreType.Huawei:
            return 'AppGallery Connect';
        case AppStoreType.Samsung:
            return 'Samsung Galaxy Store Seller Portal';
        default:
            return null;
    }
};

export const displayBetaServiceNameForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'TestFlight';
        case AppStoreType.Google:
            return 'Play Console';
        default:
            return null;
    }
};

export const displayConsoleNameWithPrefix = (appStoreType: AppStoreType) => {
    const consoleName = displayConsoleNameForAppStoreType(appStoreType);
    switch (appStoreType) {
        case AppStoreType.Amazon:
        case AppStoreType.Google:
            return `the ${consoleName}`;
        default:
            return consoleName;
    }
};

export const displayStoreNameForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'App Store';
        case AppStoreType.Google:
            return 'Play Store';
        case AppStoreType.Amazon:
            return 'Amazon Appstore';
        case AppStoreType.Huawei:
            return 'Huawei AppGallery';
        case AppStoreType.Samsung:
            return 'Samsung Galaxy Store';
        default:
            return 'app store';
    }
};

export const displayAppStoreIdTypeForAppStoreType = (appStoreType: AppStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return 'Bundle ID';
        case AppStoreType.Google:
            return 'Package name';
        case AppStoreType.Amazon:
        case AppStoreType.Huawei:
            return 'App identifier';
        case AppStoreType.Samsung:
            return 'Content ID';
        default:
            return null;
    }
};

export const displayTextForPermission = (permission: UserPermissions) => {
    switch (permission) {
        case UserPermissions.Admin:
            return 'Admin';
        case UserPermissions.RunwayAdmin:
            return 'Runway admin';
        case UserPermissions.ITAdmin:
            return 'IT admin';
        case UserPermissions.RunwayAdminReadOnly:
            return 'Runway admin (read-only)';
        default:
            return '';
    }
};

export const displayTextForBuildBucketPermission = (permission: BuildDistroMemberPermissionLevel) => {
    switch (permission) {
        case BuildDistroMemberPermissionLevel.Admin:
            return 'Admin';
        case BuildDistroMemberPermissionLevel.Uploader:
            return 'Uploader';
        case BuildDistroMemberPermissionLevel.Tester:
            return 'Tester';
        default:
            return '';
    }
};

export const displayTextForReleaseType = (releaseType: ReleaseType) => {
    switch (releaseType) {
        case ReleaseType.Major:
            return 'Major';
        case ReleaseType.Minor:
            return 'Minor';
        case ReleaseType.Point:
            return 'Point';
        case ReleaseType.Hotfix:
            return 'Hotfix';
        case ReleaseType.Rollback:
            return 'Rollback';
        default:
            return '';
    }
};

export const displayTextForAssumePointReleasesAreHotfixes = (isHotfix: boolean) => {
    return isHotfix ? 'Yes' : 'No';
};

export const displayTextForPullRequestType = (prType: PullRequestType) => {
    switch (prType) {
        case PullRequestType.BumpVersion:
            return 'Bump version';
        case PullRequestType.PromoteCode:
            return 'Promote code';
        case PullRequestType.BackmergeReleaseBranch:
            return 'Backmerge release branch';
        case PullRequestType.BackmergeReleasePRCommit:
            return 'Continuously backmerge release branch';
        case PullRequestType.CherryPickWorkingBranch:
            return 'Cherry-pick from working branch';
        case PullRequestType.HotfixCherryPick:
            return 'Hotfix cherry-pick';
        case PullRequestType.ManualConflictResolution:
            return 'Manual cherry-pick conflict resolution';
        case PullRequestType.ManualConflictResolutionOpenedByUser:
            return 'Manual cherry-pick conflict resolution (opened by user)';
        case PullRequestType.TranslationsUpdate:
            return 'Translations update';
        case PullRequestType.SyncMetadataToVCS:
            return 'Release metadata update';
        default:
            return prType;
    }
};

export const displayUserNameShort = (user: OrgUser | OverviewCodeContributor) => {
    if (user.firstName) {
        if (user.lastName) {
            return `${user.firstName} ${user.lastName.charAt(0)}.`;
        }

        return user.firstName;
    }

    return user.email;
};

export const displayUserNameLong = (user: OrgUser | OverviewCodeContributor) => {
    if (user.firstName) {
        if (user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }

        return user.firstName;
    } else if (user.email) {
        return user.email;
    } else {
        return user.id;
    }
};

// Resolves a release pilot's display name, falling back to the raw pilot id
// (an email) when the user record isn't loaded, and to a placeholder when
// there's no pilot at all.
export const displayReleasePilotName = (
    releasePilot: OrgUser | null | undefined,
    releasePilotId: string | null | undefined,
    long = false,
) => {
    if (releasePilot) {
        return long ? displayUserNameLong(releasePilot) : displayUserNameShort(releasePilot);
    }

    return releasePilotId || 'No release pilot selected';
};

export const displayUserNameNoEmail = (user: OrgUser | OverviewCodeContributor) => {
    if (!user) {
        return null;
    }
    if (user.firstName) {
        if (user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }

        return user.firstName;
    } else {
        return null;
    }
};

export const displayTextForReleaseStepType: (
    releaseStepType: ReleaseStepType | string,
    platform: AppPlatform | AppPlatform[],
    appStoreType: AppStoreType | AppStoreType[],
    isWorkflows: boolean,
) => string | null = (
    releaseStepType: string,
    platform: AppPlatform | AppPlatform[],
    appStoreType: AppStoreType | AppStoreType[],
    isWorkflows,
) => {
    switch (releaseStepType) {
        case ReleaseStepType.Kickoff:
            return 'Kickoff';
        case ReleaseStepType.FeatureReadiness:
            return 'Feature readiness';
        case ReleaseStepType.ReleaseCandidate: {
            const defaultText = displayCIText(isWorkflows);
            if (Array.isArray(platform)) {
                return defaultText;
            }

            return platform === AppPlatform.ReactNativeOTA ? 'Staging release' : defaultText;
        }
        case ReleaseStepType.RegressionTesting:
            return 'Regression testing';
        case ReleaseStepType.BetaTesting:
            return 'Beta testing';
        case ReleaseStepType.Metadata:
            return 'Metadata';
        case ReleaseStepType.Screenshots:
            return 'Screenshots';
        case ReleaseStepType.Approvals:
            return 'Approvals';
        case ReleaseStepType.Submission:
            return 'App submission';
        case ReleaseStepType.StoreReview: {
            const defaultText = 'Store review';
            if (Array.isArray(appStoreType)) {
                return defaultText;
            }

            return appStoreType ? `${displayStoreNameForAppStoreType(appStoreType)} review` : defaultText;
        }
        case ReleaseStepType.CIDistribution:
        case ReleaseStepType.VCSDistribution:
        case ReleaseStepType.Takeoff:
            return 'Release';
        case ReleaseStepType.RollbackBuild:
            return 'Rollback build';
        case ReleaseStepType.Translations:
            return 'Translations';
        case ReleaseStepType.Rollout:
            return 'Rollout';
        default:
            return null;
    }
};

export const displayTextForUserChecklistGroup = (group: UserGroup) => {
    if (!group) {
        return 'Unknown';
    }
    switch (group.id) {
        case DefaultUserGroupId.Pilot:
            return 'Active release pilot';
        default:
            return displayTextForUserGroup(group);
    }
};

export const ROLE_TITLE_BY_ASCROLE: Record<ASCRole, string> = {
    [ASCRole.AccountHolder]: 'Account Holder',
    [ASCRole.Admin]: 'Admin',
    [ASCRole.AppManager]: 'App Manager',
    [ASCRole.CustomerSupport]: 'Customer Support',
    [ASCRole.Developer]: 'Developer',
    [ASCRole.Finance]: 'Finance',
    [ASCRole.Marketing]: 'Marketing',
    [ASCRole.Sales]: 'Sales',
    // [ASCRole.AccessToReports]: 'Access to reports',
    // [ASCRole.CloudManagedAppDistribution]: 'Cloud managed app distribution',
    // [ASCRole.CloudManagedDeveloperID]: 'Cloud managed developer ID',
    // [ASCRole.CreateApps]: 'Create apps',
    // [ASCRole.ImageManager]: 'Image manager',
};

export const ROLE_TITLE_BY_GPLAY_PERMISSION: Record<GooglePlayConsolePermission, string> = {
    [GooglePlayConsolePermission.CanAccessApp]: 'View app information (read-only)',
    [GooglePlayConsolePermission.ViewFinancial]: 'View financial data',
    [GooglePlayConsolePermission.ManagePerms]: 'Admin (all permissions)',
    [GooglePlayConsolePermission.ReplyReviews]: 'Reply to reviews',
    [GooglePlayConsolePermission.ManageAPKs]: 'Release to production, exclude devices, and use Play App Signing',
    [GooglePlayConsolePermission.ManageTrackAPKs]: 'Release apps to testing tracks',
    [GooglePlayConsolePermission.ManageTrackUsers]: 'Manage testing tracks and edit tester lists',
    [GooglePlayConsolePermission.ManagePublicListing]: 'Manage store presence',
    [GooglePlayConsolePermission.ManageDraftApps]: 'Create, edit, and delete draft apps',
    [GooglePlayConsolePermission.ManageOrders]: 'Manage orders and subscriptions',
    // [GplayPermission.Unspecified]: 'Unspecified',
    // [GooglePlayConsolePermission.ManagedPlayApps]: 'Create and publish private apps to your organization',
    // [GooglePlayConsolePermission.ManagedPlaySetting]: 'Can change managed play setting',
    // [GooglePlayConsolePermission.EditGames]: 'Edit Google Play games services projects',
    // [GooglePlayConsolePermission.PublishGames]: 'Publish Google Play games services projects',
};

export const displayTextForUserGroup = (group: UserGroup) => {
    if (!group) {
        return 'Unknown';
    }
    return group.name ?? getDefaultUserGroupDisplayText(group.id);
};

export const displayTextForTimezonePreference = (
    timezonePreference: TimezonePreferenceEnum,
    userApps: AppEntity[],
    orgTimezone?: string,
): string | string[] => {
    if (timezonePreference === TimezonePreferenceEnum.Local) {
        const localTzName = dayjs.tz.guess();
        const shortTz = dayjs().tz(localTzName).format('z');
        return `${shortTz} — Local timezone`;
    }

    if (timezonePreference === TimezonePreferenceEnum.OrgWide) {
        if (!orgTimezone) return `Organization default`;
        const tzAbbr = dayjs().tz(orgTimezone).format('z');
        return `${tzAbbr} — Organization default`;
    }

    if (timezonePreference === TimezonePreferenceEnum.ScheduleSettings) {
        if (!userApps || userApps.length === 0) return `Schedule settings timezone`;

        return userApps.map((app) => {
            const tzName = getActiveScheduleTimezone(app?.appSettings?.releaseScheduleCadenceSettings);
            const appName = app?.appSettings?.appName || app?.appId;
            if (!tzName) return `UTC — via Schedule settings timezone (${appName})`;
            const tzAbbr = dayjs().tz(tzName).format('z');
            return `${tzAbbr} — via Schedule settings (${appName})`;
        });
    }

    return `Schedule settings timezone`;
};

export const getDefaultUserGroupDisplayText = (groupId: string) => {
    switch (groupId) {
        case DefaultUserGroupId.Engineer:
            return 'Engineer';
        case DefaultUserGroupId.ProductManager:
            return 'PM';
        case DefaultUserGroupId.Pilot:
            return 'Release pilot';
        case DefaultUserGroupId.Designer:
            return 'Design';
        case DefaultUserGroupId.QA:
            return 'QA';
        case DefaultUserGroupId.EngManager:
            return 'EM';
        case DefaultUserGroupId.Marketing:
            return 'Marketing';
        case DefaultUserGroupId.CX:
            return 'CX';
        case DefaultUserGroupId.Ops:
            return 'Ops';
        case DefaultUserGroupId.Approver:
            return 'Approver';
        default:
            return '';
    }
};

export const displayTextForUserGroupSingular = (group: UserGroup) => {
    if (!group) {
        return 'Unknown';
    }

    const groupName = getDefaultUserGroupDisplayText(group.id);
    if (groupName !== '') {
        return groupName;
    }

    return group.name;
};

export const displayTextForUserChecklistGroups = (userGroups: UserGroup[]) => {
    const displayTexts = userGroups.map((group) => {
        return displayTextForUserChecklistGroup(group);
    });

    return displayTexts.join(', ');
};

export const statusStringForReleaseStatus = (status: ReleaseStatus) => {
    switch (status) {
        case ReleaseStatus.Active:
            return 'Active';
        case ReleaseStatus.Completed:
            return 'Completed';
        case ReleaseStatus.Skipped:
            return 'Skipped';
        default:
            return null;
    }
};

export const statusColorForLocalizationStatus = (status: LocalizationStatus) => {
    switch (status) {
        case LocalizationStatus.Pending:
        case LocalizationStatus.PendingTranslations:
            return runwayColors.statusYellow;
        case LocalizationStatus.Ready:
            return runwayColors.statusGreen;
        case LocalizationStatus.Blocked:
            return runwayColors.statusRed;
    }
};

export const buildStatusStringForBuild: (buildInfo?: BuildInfo) => string = (buildInfo) => {
    if (!buildInfo) {
        return 'Not started';
    }
    if (buildInfo?.providerBuildStatusString !== '') {
        return buildInfo?.providerBuildStatusString;
    }
    return displayTextForBuildStatus(buildInfo.buildStatus);
};

export const displayTextForBuildStatus: (status?: BuildStatus) => string = (status) => {
    switch (status) {
        case BuildStatus.InProgress:
            return 'In progress';
        case BuildStatus.Failure:
            return 'Failed';
        case BuildStatus.Success:
            return 'Succeeded';
        case BuildStatus.Stopped:
            return 'Stopped';
        default:
            return 'Not started';
    }
};

export const statusColorForBuildStatus: (status?: BuildStatus) => string = (status?: BuildStatus) => {
    switch (status) {
        case BuildStatus.InProgress:
            return runwayColors.statusYellow;
        case BuildStatus.Failure:
            return runwayColors.statusRed;
        case BuildStatus.Success:
            return runwayColors.statusGreen;
        case BuildStatus.Stopped:
            return runwayColors.statusGrey;
        default:
            return runwayColors.statusGrey;
    }
};

export const statusColorForReviewSubmissionState = (state: AppStoreReviewSubmissionState) => {
    switch (state) {
        case AppStoreReviewSubmissionState.ReadyForReview:
        case AppStoreReviewSubmissionState.WaitingForReview:
        case AppStoreReviewSubmissionState.InReview:
        case AppStoreReviewSubmissionState.Completing:
            return runwayColors.statusYellow;
        case AppStoreReviewSubmissionState.UnresolvedIssues:
        case AppStoreReviewSubmissionState.Canceling:
        case AppStoreReviewSubmissionState.Blocked:
            return runwayColors.statusRed;
        case AppStoreReviewSubmissionState.Complete:
            return runwayColors.statusGreen;
        default:
            return runwayColors.statusGrey;
    }
};

export const reviewItemNameForType = (type: AppStoreReviewSubmissionItemType) => {
    switch (type) {
        case AppStoreReviewSubmissionItemType.CustomProductPage:
            return 'Custom product page';
        case AppStoreReviewSubmissionItemType.AppEvent:
            return 'In-app event';
        case AppStoreReviewSubmissionItemType.AppStoreVersionExperiment:
            return 'Product optimization';
        case AppStoreReviewSubmissionItemType.AppBuildVersion:
            return 'Build';
        case AppStoreReviewSubmissionItemType.InAppPurchase:
            return 'In-app purchase';
        case AppStoreReviewSubmissionItemType.Subscription:
            return 'Subscription';
        case AppStoreReviewSubmissionItemType.SubscriptionGroup:
            return 'Subscription group';
        case AppStoreReviewSubmissionItemType.BackgroundAsset:
            return 'Background asset';
        case AppStoreReviewSubmissionItemType.GameCenterAchievement:
            return 'Game Center achievement';
        case AppStoreReviewSubmissionItemType.GameCenterActivity:
            return 'Game Center activity';
        case AppStoreReviewSubmissionItemType.GameCenterChallenge:
            return 'Game Center challenge';
        case AppStoreReviewSubmissionItemType.GameCenterLeaderboard:
            return 'Game Center leaderboard';
        case AppStoreReviewSubmissionItemType.GameCenterLeaderboardSet:
            return 'Game Center leaderboard set';
        default:
            return 'Unknown';
    }
};

export const displayInAppPurchaseType = (type: InAppPurchaseType) => {
    switch (type) {
        case InAppPurchaseType.Consumable:
            return 'Consumable';
        case InAppPurchaseType.NonConsumable:
            return 'Non-consumable';
        case InAppPurchaseType.NonRenewingSubscription:
            return 'Non-renewing subscription';
        default:
            return type;
    }
};

export const displayReviewItemStatus = (status: AppStoreReviewSubmissionItemState) => {
    switch (status) {
        case AppStoreReviewSubmissionItemState.InReview:
            return 'In review';
        case AppStoreReviewSubmissionItemState.ReadyForReview:
            return 'Ready for review';
        case AppStoreReviewSubmissionItemState.Accepted:
            return 'Accepted';
        case AppStoreReviewSubmissionItemState.Approved:
            return 'Approved';
        case AppStoreReviewSubmissionItemState.Rejected:
            return 'Rejected';
        case AppStoreReviewSubmissionItemState.Removed:
            return 'Removed';
    }
};

export const secondaryTextForBuildStatus: (
    status?: BuildStatus,
    buildDuration?: string,
    buildNumber?: string,
    providerBuildStatusString?: string,
) => string = (status, buildDuration, buildNumber, providerBuildStatusString) => {
    switch (status) {
        case BuildStatus.Success:
            return `Build #${buildNumber} succeeded.${buildDuration ? ` Build duration: ${buildDuration}.` : ''}`;
        case BuildStatus.Failure:
            return `Build #${buildNumber} failed.${buildDuration ? ` Build duration: ${buildDuration}.` : ''}`;
        case BuildStatus.InProgress:
            return `Build #${buildNumber} is currently in progress.`;
        case BuildStatus.Stopped: {
            const statusText =
                providerBuildStatusString && providerBuildStatusString.toLowerCase() !== 'stopped' ?
                    `status is '${providerBuildStatusString}'`
                :   'stopped';
            return `Build #${buildNumber} ${statusText}.${buildDuration ? ` Build duration: ${buildDuration}.` : ''}`;
        }
        default:
            return 'No build runs detected. Run workflow to start a build.';
    }
};

export const statusColorForAppStoreIsProcessedStatus = (isProcessed: boolean) => {
    return isProcessed ? runwayColors.statusGreen : runwayColors.statusYellow;
};

export const shortCommitHashFromHash = (hash?: string) => {
    return hash?.substring(0, 7);
};

export const displayTextForAutomationStatus = (
    status: AutomationStatus,
    isAutomationRetrying?: boolean,
    isAutomationRecurring?: boolean,
) => {
    if (isAutomationRetrying) {
        return 'Retrying...';
    }

    if (isAutomationRecurring && status !== AutomationStatus.Failed) {
        return 'Active';
    }

    switch (status) {
        case AutomationStatus.Success:
            return 'Completed';
        case AutomationStatus.Failed:
            return 'Failed';
        case AutomationStatus.Incomplete:
            return 'Incomplete';
        case AutomationStatus.Pending:
            return 'Pending';
        case AutomationStatus.Skipped:
            return 'Skipped';
        case AutomationStatus.Inactive:
            return 'Inactive';
        case AutomationStatus.Active:
            return 'Active';
        case AutomationStatus.Paused:
            return 'Paused';
        case AutomationStatus.Blocked:
            return 'Blocked';
        case AutomationStatus.Retrying:
            return 'Retrying...';
        case AutomationStatus.InProgress:
            return 'In progress';
    }
};

export const colorForAutomationStatus: (
    status: AutomationStatus,
    isLifecycle: boolean,
    isRetrying?: boolean,
    isPending?: boolean,
) => string = (status, isLifecycle, isRetrying, isPending) => {
    if (isRetrying || isPending) {
        return runwayColors.statusYellow;
    }

    switch (status) {
        case AutomationStatus.Active:
            return runwayColors.info;
        case AutomationStatus.Success:
            return runwayColors.statusGreen;
        case AutomationStatus.Blocked:
        case AutomationStatus.Failed:
            return runwayColors.statusRed;
        case AutomationStatus.Retrying:
        case AutomationStatus.Incomplete:
        case AutomationStatus.InProgress:
            return runwayColors.statusYellow;
        case AutomationStatus.Pending:
            return isLifecycle ? runwayColors.statusYellow : runwayColors.info;
        case AutomationStatus.Paused:
        case AutomationStatus.Inactive:
        case AutomationStatus.Skipped:
            return antGrey.grey6;
    }
};

export const imperativeDescriptionTextForAutomationType: (automationType: AutomationType) => string = (
    automationType,
) => {
    switch (automationType) {
        case AutomationType.AutoKickoffRelease:
            return 'Scheduled to kick off';
        case AutomationType.AutoSubmitRelease:
            return 'Scheduled to submit for review';
        case AutomationType.AutoRelease:
            return 'Scheduled to release';
        case AutomationType.AutoBumpVersion:
            return 'Scheduled to bump version';
        default:
            return 'Scheduled to perform';
    }
};

export const iconForAutomationType: (automationType: AutomationType) => React.ComponentType = (automationType) => {
    switch (automationType) {
        case AutomationType.AutoSelectLatestBuild:
        case AutomationType.AutoSelectLatestExpoUpdateGroup:
            return ToolOutlined;
        case AutomationType.AutoSyncAppleDevices:
            return SyncOutlined;
        case AutomationType.AutoSyncAppleProvisioningProfilesDevices:
            return SyncOutlined;
        case AutomationType.AutoCreateAppStoreVersions:
            return FieldBinaryOutlined;
        case AutomationType.AutoCreateRunwayReleases:
            return PlusOutlined;
        case AutomationType.AutoTagReleases:
            return TagOutlined;
        case AutomationType.AutoMergeRunwayPRs:
            return PullRequestOutlined;
        case AutomationType.AutoAddMissingFAs:
            return TagsOutlined;
        case AutomationType.AutoDownloadArtifacts:
            return DownloadOutlined;
        case AutomationType.AutoUploadReleaseAssets:
            return PaperClipOutlined;
        case AutomationType.AutoApplyMetadata:
            return FileTextOutlined;
        case AutomationType.AutoKickoffRelease:
            return CalendarOutlined;
        case AutomationType.AutoSubmitRelease:
            return SearchOutlined;
        case AutomationType.AutoPullSubmissionFromReview:
            return UndoOutlined;
        case AutomationType.AutoRelease:
            return RocketOutlined;
        case AutomationType.AutoBumpVersion:
            return FieldBinaryOutlined;
        case AutomationType.AutoAssignBetaBuilds:
            return UsergroupAddOutlined;
        case AutomationType.AutoSubmitBetaBuilds:
            return SearchOutlined;
        case AutomationType.AutoPromoteBuildsToTestTrack:
            return ToTopOutlined;
        case AutomationType.AutoPromoteStablePhasedReleases:
            return RiseOutlined;
        case AutomationType.AutoHaltUnstablePhasedReleases:
            return StopOutlined;
        case AutomationType.AutoCreateReleaseChannels:
            return CommentOutlined;
        case AutomationType.AutoCompleteIssueTrackingReleases:
            return CheckCircleOutlined;
        case AutomationType.AutoApplyReviewAttachments:
        case AutomationType.AutoPopulateAppClipDefaultInfo:
            return PaperClipOutlined;
        case AutomationType.AutoBackmergeReleaseBranches:
            return PullRequestOutlined;
        case AutomationType.AutoApplyBetaBuildNotes:
            return FileTextOutlined;
        case AutomationType.AutoGenerateCIBuildDiffSummary:
            return DiffOutlined;
        case AutomationType.AutoKickoffTriggerWorkflow:
            return RocketOutlined;
        case AutomationType.AutoTriggerCIWorkflow:
            return RocketOutlined;
        case AutomationType.AutoCreateIncidents:
            return RocketOutlined;
        case AutomationType.AutoMarkReleaseLiveInStabilityMonitoringAndOBAA:
            return RocketOutlined;
        case AutomationType.AutoSyncAppStoreUsers:
            return SyncOutlined;
        case AutomationType.AutoDeleteReleaseBranch:
            return DeleteRowOutlined;
        case AutomationType.AutoUploadBinaryArtifactToAppStore:
            return UploadOutlined;
        case AutomationType.AutoUploadDsymsToStabilityMonitoring:
            return FileZipOutlined;
        case AutomationType.AutoCherryPickFromWorkingBranch:
            return PullRequestOutlined;
        case AutomationType.AutoStagedRollout:
            return RiseOutlined;
        case AutomationType.AutoTransitionIssueTrackingTickets:
            return CheckCircleOutlined;
        case AutomationType.AutoDistributeReleaseSummary:
            return ProfileOutlined;
        case AutomationType.AutoResignRollbackBuild:
            return KeyOutlined;
        case AutomationType.AutoPrepareRollbackRelease:
            return UndoOutlined;
        case AutomationType.AutoCherryPickLocalizableStrings:
            return PullRequestOutlined;
        case AutomationType.AutoSyncTranslations:
            return SyncOutlined;
        case AutomationType.AutoSyncMetadataTranslations:
            return SyncOutlined;
        case AutomationType.AutoSyncMetadataFromVCS:
            return SyncOutlined;
        case AutomationType.AutoSyncSlackGroups:
        case AutomationType.AutoSyncSlackPilotUserGroup:
        case AutomationType.AutoSyncTeamsTags:
        case AutomationType.AutoSyncTeamsPilotTag:
            return UserSwitchOutlined;
        case AutomationType.AutoCreateTestRuns:
            return ReconciliationOutlined;
        case AutomationType.AutoTranslateUserReviews:
            return TranslationOutlined;
        case AutomationType.AutoPostBuildInfoOnPMTickets:
            return CommentOutlined;
        case AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest:
            return InboxOutlined;
        case AutomationType.AutoAssignTriageIssue:
            return InboxOutlined;
        case AutomationType.AutoApplyLabelsToFixTickets:
            return TagsOutlined;
        // Bucket automations
        case AutomationType.BAAutoUpdatePRWithArtifactLink:
            return QrcodeOutlined;
        case AutomationType.BAAutoGenerateBuildTesterNotes:
            return FileTextOutlined;
        case AutomationType.BAAutoTriggerBuildSystemWorkflow:
            return PlayCircleOutlined;
        case AutomationType.BAAutoUploadBinaryArtifactToAppStore:
            return UploadOutlined;
        case AutomationType.BAAutoPostBuildInfoOnPMTickets:
            return CommentOutlined;
        case AutomationType.BAAutoAssignBetaBuilds:
            return UsergroupAddOutlined;
    }
};

export type AllNotificationTypes = AppNotificationType | BuildDistroNotificationType | EmailNotificationType;

export const iconForAppNotificationId: (appNotificationId: AllNotificationTypes) => React.ComponentType = (
    appNotificationId,
) => {
    switch (appNotificationId) {
        case AppNotificationType.DailyUserReviewsDigest:
        case AppNotificationType.IndividualReview:
        case AppNotificationType.WeeklyUserReviewsIssuesDigest:
            return StarOutlined;
        case AppNotificationType.ReleaseAnnouncement:
        case AppNotificationType.ManualReleaseToAppStore:
        case AppNotificationType.AutoReleaseToAppStore:
        case AppNotificationType.ReleaseWentLiveGooglePlayParsedEmail:
            return RocketOutlined;
        case AppNotificationType.TargetKickoffReminder:
        case AppNotificationType.ReleaseKickoff:
        case AppNotificationType.PauseResumeScheduleCadence:
        case AppNotificationType.TargetSubmitReminder:
        case AppNotificationType.TargetReleaseReminder:
            return CalendarOutlined;
        case AppNotificationType.BumpVersionPRMerged:
            return PullRequestOutlined;
        case AppNotificationType.NextUpPilotReminder:
            return UserOutlined;
        case AppNotificationType.AppStorePhasedReleaseComplete:
        case AppNotificationType.BuildAvail:
        case AppNotificationType.BuildAvailArtifacts:
        case AppNotificationType.ProductionWorkflowSucceeded:
        case AppNotificationType.StepApproval:
        case AppNotificationType.ConfigFileApplied:
        case AppNotificationType.FrReady:
        case AppNotificationType.BetaSoakEnd:
        case AppNotificationType.BetaSoakManualEnd:
        case AppNotificationType.ReleaseComplete:
        case AppNotificationType.VcsDistributionPublished:
            return CheckCircleOutlined;
        case AppNotificationType.BetaSubmittedForReview:
        case AppNotificationType.AutoSubmitToAppStore:
        case AppNotificationType.ManualSubmitToAppStore:
            return SearchOutlined;
        case AppNotificationType.ReleaseKickoffFailed:
        case AppNotificationType.AutoSubmitFailedIncompleteSteps:
        case AppNotificationType.AutoSubmitFailed:
        case AppNotificationType.AutoReleaseFailedIncompleteSteps:
        case AppNotificationType.AutoReleaseFailed:
        case AppNotificationType.BuildFailed:
        case AppNotificationType.ReleaseAutotagFail:
        case AppNotificationType.AutoMergeFailure:
        case AppNotificationType.GoogleCustomStageRolloutFailedToStart:
            return CloseCircleOutlined;
        case AppNotificationType.AppStoreStatusUpdate:
            return AppstoreOutlined;
        case AppNotificationType.AppStorePhasedReleaseUpdate:
        case AppNotificationType.StablePhasedReleasePromoted:
        case AppNotificationType.GoogleCustomStagedRolloutManuallyStarted:
            return RiseOutlined;
        case AppNotificationType.BuildStopped:
        case AppNotificationType.UnstablePhasedReleaseHalted:
            return PauseCircleOutlined;
        case AppNotificationType.BuildStarted:
            return PlayCircleOutlined;
        case AppNotificationType.ReleaseTagged:
        case AppNotificationType.FrNotReady:
            return TagOutlined;
        case AppNotificationType.FaApplied:
            return TagsOutlined;
        case AppNotificationType.MetadataEditable:
            return EditOutlined;
        case AppNotificationType.MetaDataApplied:
            return FileTextOutlined;
        case AppNotificationType.SetSelectedBuildAppStore:
            return AppstoreAddOutlined;
        case AppNotificationType.ChecklistItemComment:
            return MessageOutlined;
        case AppNotificationType.ChecklistItemsReminder:
        case AppNotificationType.RegressionItemsReminder:
        case AppNotificationType.ApprovalItemsReminder:
            return CarryOutOutlined;
        case AppNotificationType.ChecklistItemOwnerChanged:
            return TeamOutlined;
        case AppNotificationType.BetaReadyForInternalTesting:
        case AppNotificationType.BetaReadyForExternalTesting:
        case AppNotificationType.BetaBuildAvail:
            return ShakeOutlined;
        case AppNotificationType.BetaSoakStart:
            return ClockCircleOutlined;
        case AppNotificationType.RegStatusUpdate:
            return BarsOutlined;
        case AppNotificationType.HealthMetricsWarning:
        case AppNotificationType.HealthMetricsSkippedForStaleData:
        case AppNotificationType.ASCMissingOrExpiredAgreements:
        case AppNotificationType.BundleSizeIncrease:
        case AppNotificationType.AutoCreatedIncident:
            return WarningOutlined;
        case AppNotificationType.ChecklistItemIncomplete:
        case AppNotificationType.ChecklistItemReminder:
            return CloseOutlined;
        case AppNotificationType.ChecklistItemApproved:
        case AppNotificationType.BulkApproveChecklistItems:
            return CheckOutlined;
        case AppNotificationType.AutoUploadBuildToAppStoreFailed:
        case AppNotificationType.AutoUploadBuildToAppStoreSucceeded:
            return UploadOutlined;
        case AppNotificationType.FeatureFlagToggled:
            return FlagOutlined;
        case BuildDistroNotificationType.NewBuildsAvailable:
            return ToolOutlined;
        case AppNotificationType.ForwardedAppStoreEmail:
            return InboxOutlined;
        case AppNotificationType.TriageIssueStatusUpdated:
        case AppNotificationType.TriageIssueDetected:
            return InboxOutlined;
        case EmailNotificationType.OrgMetricsDigest:
        case EmailNotificationType.OrgReleasesDigest:
        case EmailNotificationType.AppReleaseDigest:
        case EmailNotificationType.OrgOverviewSummary:
        case EmailNotificationType.ReleaseSummary:
            return MailOutlined;
        default:
            return BellOutlined;
    }
};

export const iconForCapability: (capability: IntegrationCapability) => React.ComponentType = (capability) => {
    switch (capability) {
        case IntegrationCapability.AppStore:
            return AppstoreOutlined;
        case IntegrationCapability.FeatureManagement:
            return TagOutlined;
        case IntegrationCapability.VersionControl:
            return CodeOutlined;
        case IntegrationCapability.BetaTesting:
            return ClockCircleOutlined;
        case IntegrationCapability.Notifications:
            return NotificationOutlined;
        case IntegrationCapability.BuildSystem:
            return ToolOutlined;
        case IntegrationCapability.SDKDistribution:
            return FileZipOutlined;
        case IntegrationCapability.StabilityMonitoring:
            return BugOutlined;
        case IntegrationCapability.RegressionTesting:
            return ShakeOutlined;
        case IntegrationCapability.ObservabilityAndAnalytics:
            return BarChartOutlined;
        case IntegrationCapability.FeatureFlagging:
            return FlagOutlined;
        case IntegrationCapability.Translations:
            return TranslationOutlined;
        case IntegrationCapability.Scheduling:
            return CalendarOutlined;
        case IntegrationCapability.Calendar:
            return CalendarOutlined;
    }
};

export const displayTextForBetaBuildStatus = (status: BetaStatus, details?: AppBetaBuildInfo) => {
    switch (status) {
        case BetaStatus.Testing: {
            const googleBetaBuildDetail = details as GoogleBetaBuildDetail;
            if (googleBetaBuildDetail && googleBetaBuildDetail.userFraction) {
                const userFractionText = displayStringForRolloutPercentage(
                    convertAndroidStagedRolloutPercentage(googleBetaBuildDetail.userFraction),
                );
                return `In testing (${userFractionText} rollout)`;
            } else if (googleBetaBuildDetail && googleBetaBuildDetail.status === GooglePlayStoreState.Completed) {
                return `In testing (100% rollout)`;
            }
            return 'In testing';
        }
        case BetaStatus.Processing:
            return 'Processing';
        case BetaStatus.NotTesting:
            return 'Not testing';
        case BetaStatus.Error:
            return 'Error';
        case BetaStatus.NeedsReview:
            return 'Needs review';
        case BetaStatus.WaitingForReview:
            return 'Awaiting review';
        case BetaStatus.InReview:
            return 'In review';
        default:
            return 'Not testing';
    }
};

export const colorForBetaBuildStatus = (status?: BetaStatus) => {
    switch (status) {
        case BetaStatus.Testing:
            return runwayColors.statusGreen;
        case BetaStatus.NotTesting:
            return runwayColors.statusGrey;
        case BetaStatus.NeedsReview:
        case BetaStatus.WaitingForReview:
        case BetaStatus.InReview:
        case BetaStatus.Processing:
            return runwayColors.statusYellow;
        case BetaStatus.Error:
            return runwayColors.statusRed;
        default:
            return runwayColors.statusGrey;
    }
};

export const displayTextForAppleExternalBetaBuildState = (state?: AppleExternalBetaBuildState) => {
    switch (state) {
        case AppleExternalBetaBuildState.Processing:
            return 'Processing';
        case AppleExternalBetaBuildState.ProcessingException:
            return 'Processing exception';
        case AppleExternalBetaBuildState.MissingExportCompliance:
            return 'Missing export compliance';
        case AppleExternalBetaBuildState.ReadyForBetaTesting:
            return 'Ready for beta testing';
        case AppleExternalBetaBuildState.InBetaTesting:
            return 'In testing';
        case AppleExternalBetaBuildState.Expired:
            return 'Expired';
        case AppleExternalBetaBuildState.ReadyForBetaSubmission:
            return 'Ready for beta review submission';
        case AppleExternalBetaBuildState.InExportCompliance:
            return 'In export compliance';
        case AppleExternalBetaBuildState.WaitingForBetaReview:
            return 'Waiting for beta review';
        case AppleExternalBetaBuildState.InBetaReview:
            return 'In beta review';
        case AppleExternalBetaBuildState.BetaRejected:
            return 'Beta review rejected';
        case AppleExternalBetaBuildState.BetaApproved:
            return 'Beta review approved';
        default:
            return 'Unknown';
    }
};

export const displayTextForAppleInternalBetaBuildState = (state?: AppleInternalBetaBuildState) => {
    switch (state) {
        case AppleInternalBetaBuildState.Processing:
            return 'Processing...';
        case AppleInternalBetaBuildState.ProcessingException:
            return 'Processing exception';
        case AppleInternalBetaBuildState.MissingExportCompliance:
            return 'Missing export compliance';
        case AppleInternalBetaBuildState.ReadyForBetaTesting:
            return 'Ready for beta testing';
        case AppleInternalBetaBuildState.InBetaTesting:
            return 'In testing';
        case AppleInternalBetaBuildState.Expired:
            return 'Expired';
        case AppleInternalBetaBuildState.InExportCompliance:
            return 'In export compliance';
        default:
            return 'Unknown';
    }
};

export const displayDescriptionForAppleExternalBuildState = (
    buildNumber: string,
    state?: AppleExternalBetaBuildState,
) => {
    switch (state) {
        case AppleExternalBetaBuildState.Processing:
            return `Build #${buildNumber} is processing in preparation for beta testing.`;
        case AppleExternalBetaBuildState.ProcessingException:
            return `Build #${buildNumber} has encountered a processing exception.`;
        case AppleExternalBetaBuildState.MissingExportCompliance:
            return `Build #${buildNumber} is missing export compliance settings. Please review export compliance options in TestFlight.`;
        case AppleExternalBetaBuildState.ReadyForBetaTesting:
            return `Build #${buildNumber} is ready for beta testing.`;
        case AppleExternalBetaBuildState.InBetaTesting:
            return `Build #${buildNumber} is in beta testing.`;
        case AppleExternalBetaBuildState.Expired:
            return `Build #${buildNumber} has reached its build expiry of 90 days.`;
        case AppleExternalBetaBuildState.ReadyForBetaSubmission:
            return `Build #${buildNumber} is ready for beta review submission. You must submit your beta build for beta review in order to begin testing with external testing groups.`;
        case AppleExternalBetaBuildState.InExportCompliance:
            return `Build #${buildNumber} is in export compliance.`;
        case AppleExternalBetaBuildState.WaitingForBetaReview:
            return `Build #${buildNumber} is in the queue to undergo beta review by Apple.`;
        case AppleExternalBetaBuildState.InBetaReview:
            return `Build #${buildNumber} is currently in beta review.`;
        case AppleExternalBetaBuildState.BetaRejected:
            return `Build #${buildNumber} has been rejected.`;
        case AppleExternalBetaBuildState.BetaApproved:
            return `Build #${buildNumber} has been approved for beta testing.`;
        default:
            return `The status of build #${buildNumber} is unknown.`;
    }
};

export const displayDescriptionForAppleInternalBuildState = (
    buildNumber: string,
    state?: AppleInternalBetaBuildState,
) => {
    switch (state) {
        case AppleInternalBetaBuildState.Processing:
            return `Build #${buildNumber} is processing in preparation for beta testing.`;
        case AppleInternalBetaBuildState.ProcessingException:
            return `Build #${buildNumber} has encountered a processing exception.`;
        case AppleInternalBetaBuildState.MissingExportCompliance:
            return `Build #${buildNumber} is missing export compliance settings. Please review export compliance options in TestFlight.`;
        case AppleInternalBetaBuildState.ReadyForBetaTesting:
            return `Build #${buildNumber} is ready for beta testing.`;
        case AppleInternalBetaBuildState.InBetaTesting:
            return `Build #${buildNumber} is in beta testing.`;
        case AppleInternalBetaBuildState.Expired:
            return `Build #${buildNumber} has reached its build expiry of 90 days.`;
        case AppleInternalBetaBuildState.InExportCompliance:
            return `Build #${buildNumber} is in export compliance.`;
        default:
            return `The status of build #${buildNumber} is unknown.`;
    }
};

export const displayTextForBetaSoakStatus = (soakStatus: BetaSoakStatus) => {
    switch (soakStatus) {
        case BetaSoakStatus.NotTesting:
            return 'Not started';
        case BetaSoakStatus.Testing:
        case BetaSoakStatus.TestingNonBlocking:
            return 'Beta soak in progress';
        case BetaSoakStatus.TestingComplete:
            return 'Testing complete';
    }
};

export const displayColorForBetaSoakStatus = (soakStatus: BetaSoakStatus) => {
    switch (soakStatus) {
        case BetaSoakStatus.NotTesting:
            return runwayColors.statusGrey;
        case BetaSoakStatus.Testing:
        case BetaSoakStatus.TestingNonBlocking:
            return runwayColors.statusYellow;
        case BetaSoakStatus.TestingComplete:
            return runwayColors.statusGreen;
    }
};

export const displayDescriptionForBetaSoakStatus = (soakStatus: BetaSoakStatus, buildNumber?: string) => {
    switch (soakStatus) {
        case BetaSoakStatus.NotTesting:
            return `Beta soak period has not started. ${
                buildNumber ?
                    `Distribute build #${buildNumber} for testing to start beta testing.`
                :   'Upload a build to start beta testing.'
            }`;
        case BetaSoakStatus.Testing:
        case BetaSoakStatus.TestingNonBlocking:
            return `Build #${buildNumber} is in testing and the beta soak period has started.`;
        case BetaSoakStatus.TestingComplete:
            return `Build #${buildNumber} is has finished testing and the beta soak period is complete.`;
    }
};

export const displayDescriptionForBetaSoakStatusMultipleBuilds = (
    soakStatus: BetaSoakStatus,
    buildNumbers?: string[],
) => {
    switch (soakStatus) {
        case BetaSoakStatus.NotTesting:
            return `Beta soak period has not started. ${
                buildNumbers ?
                    `Distribute builds ${buildNumbers.join(', ')} for testing to start beta testing.`
                :   `Distribute builds for testing to start beta testing.`
            }`;
        case BetaSoakStatus.Testing:
        case BetaSoakStatus.TestingNonBlocking:
            return `All builds (${buildNumbers!.join(', ')}) are in testing and the beta soak period has started.`;
        case BetaSoakStatus.TestingComplete:
            return `All builds (${buildNumbers!.join(
                ', ',
            )}) have finished testing and the beta soak period is complete.`;
    }
};

export const displayTitleForGoogleBetaBuildState = (state?: GooglePlayStoreState) => {
    switch (state) {
        case GooglePlayStoreState.Completed:
            return 'Completed';
        case GooglePlayStoreState.InProgress:
            return 'In progress';
        case GooglePlayStoreState.Halted:
            return 'Halted';
        case GooglePlayStoreState.Draft:
            return 'In draft';
        case GooglePlayStoreState.StatusUnspecified:
            return 'Status unspecified';
        default:
            return 'Not found on testing track';
    }
};

export const displayDescriptionForGoogleBetaBuildState = (
    track: string,
    state?: GooglePlayStoreState,
    buildNumber?: string,
    userFraction?: number,
) => {
    if (!state) {
        return `Version code #${buildNumber} is not found on the ${track} track. Promote the version code to the ${track} track to start beta testing.`;
    }

    switch (state) {
        case GooglePlayStoreState.Completed:
            return `Version code #${buildNumber} has been rolled out to all users on the ${track} track.`;
        case GooglePlayStoreState.InProgress: {
            const userFractionText = displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(userFraction!),
            );
            return `Version code #${buildNumber} is being rolled out to ${userFractionText} of users on the ${track} track.`;
        }
        case GooglePlayStoreState.Halted:
            return `The rollout of version code #${buildNumber} has been halted. Resume rollout to continue testing.`;
        case GooglePlayStoreState.Draft:
            return `The rollout of version code #${buildNumber} has not yet started. Start the rollout to begin testing.`;
        case GooglePlayStoreState.StatusUnspecified:
            return 'No status can be found for this version code.';
    }
};

export const displayTextForAppStoreUploadRule: (rule: AppStoreArtifactUploadRule) => string = (
    rule: AppStoreArtifactUploadRule,
) => {
    switch (rule) {
        case AppStoreArtifactUploadRule.ContinueOnFailure:
            return 'Upload artifacts for all builds – continue uploads on failure';
        case AppStoreArtifactUploadRule.StopOnFailure:
            return 'Upload artifacts for all builds – stop uploads on failure';
        case AppStoreArtifactUploadRule.UploadNewestBuildOnly:
            return 'Upload artifacts for newest build only';
    }
};

export const displayTextForAutoApplyBuildNotesStrategy: (strategy: BuildNotesAutomationStrategy) => string = (
    strategy: BuildNotesAutomationStrategy,
) => {
    switch (strategy) {
        case BuildNotesAutomationStrategy.CarryOver:
            return 'Carry over from previous beta build';
        case BuildNotesAutomationStrategy.CarryOverFromPreviousRelease:
            return 'Carry over from previous release beta build';
        case BuildNotesAutomationStrategy.BuildChangelog:
            return 'Generate changelog from diff since previous beta build';
        case BuildNotesAutomationStrategy.ReleaseChangelog:
            return 'Generate changelog from diff since last completed release';
        case BuildNotesAutomationStrategy.CIBuildVariable:
            return 'Generate notes from CI build workflow argument';
        case BuildNotesAutomationStrategy.CIBuildCommitMessage:
            return 'Generate notes from CI build commit message';
        case BuildNotesAutomationStrategy.AIBuildSummary:
            return 'Generate AI summary of diff since previous beta build';
        case BuildNotesAutomationStrategy.AIReleaseSummary:
            return 'Generate AI summary of diff since last completed release';
    }
};

export const LIFECYCLE_AUTOMATION_TIMING_MESSAGE =
    'This automation will execute within 5 minutes after the target date. It does not fire immediately at the target time.';

export const displayTextForAutoReleaseStrategy: (strategy: AutoReleaseStrategy) => string = (
    strategy: AutoReleaseStrategy,
) => {
    switch (strategy) {
        case AutoReleaseStrategy.OnDate:
            return 'On or after target date';
        case AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate:
            return 'On or after target date, if all previous steps are ready';
        case AutoReleaseStrategy.OnceStepsComplete:
            return 'As soon as possible, if all previous steps are ready';
    }
};

export const displayTextForResigningStrategy: (strategy: RollbackResigningStrategy) => string = (
    strategy: RollbackResigningStrategy,
) => {
    switch (strategy) {
        case RollbackResigningStrategy.LocalResign:
            return 'Re-sign by Runway';
        case RollbackResigningStrategy.CIResign:
            return 'Rebuild by your CI';
    }
};

export const displayTextForAutoSubmitStrategy: (strategy: AutoSubmitStrategy) => string = (
    strategy: AutoSubmitStrategy,
) => {
    switch (strategy) {
        case AutoSubmitStrategy.AfterTargetDateEvenIfNotReady:
            return 'On the target date';
        case AutoSubmitStrategy.OnceStepsCompleteAfterTargetDate:
            return 'On or after target date, if all previous steps are ready';
        case AutoSubmitStrategy.OnceStepsComplete:
            return 'As soon as possible, if all previous steps are ready';
        case AutoSubmitStrategy.OnTargetDate:
            return 'On the target date, if all previous steps are ready';
        case AutoSubmitStrategy.AsSoonAsPossible:
            return 'As soon as possible';
    }
};

export const displayTextForRetainedBuildsStrategy: (strategy: RetainedBuildsStrategy) => string = (
    strategy: RetainedBuildsStrategy,
) => {
    switch (strategy) {
        case RetainedBuildsStrategy.FromPreviousRelease:
            return 'Carry over the builds retained by the previous release';
        case RetainedBuildsStrategy.FromSelection:
            return 'Always retain a specific set of builds';
        case RetainedBuildsStrategy.None:
            return 'Never retain builds';
    }
};

export const helperTextForAutoSubmitStrategy: (strategy: AutoSubmitStrategy, ast: AppStoreType) => React.ReactNode = (
    strategy: AutoSubmitStrategy,
    ast: AppStoreType,
) => {
    switch (strategy) {
        case AutoSubmitStrategy.AfterTargetDateEvenIfNotReady:
            return 'Runway will submit for review on the target date without requiring previous steps be ready.';
        case AutoSubmitStrategy.AsSoonAsPossible:
            switch (ast) {
                case AppStoreType.Apple:
                    return 'Runway will submit for review as soon as an uploaded build is detected and all other submission requirements are met.';
                case AppStoreType.Google:
                    return 'Runway will submit for review as soon as a build is detected on the Production track and all other submission requirements are met.';
                default:
                    return 'Runway will submit for review as soon as an app store build is detected that meets submission requirements.';
            }
        default:
            return undefined;
    }
};

export const warningTextForAutoSubmitStrategy: (
    strategy: AutoSubmitStrategy,
    ast: AppStoreType,
) => string | undefined = (strategy: AutoSubmitStrategy, ast: AppStoreType) => {
    if (strategy === AutoSubmitStrategy.AsSoonAsPossible && ast === AppStoreType.Google) {
        return `If managed publishing is disabled, submitted versions will be released immediately to users as soon as they're approved.`;
    }
    return undefined;
};

export const displayTextForReleaseNotesDataSource: (
    dataSource: ReleaseNotesDataSource,
    platform: AppPlatform,
) => string = (dataSource, platform) => {
    switch (dataSource) {
        case ReleaseNotesDataSource.PreviousRelease:
            return 'Pull in from previous release';
        case ReleaseNotesDataSource.DefaultReleaseNotes:
            return `Use default ${isIosPlatform(platform) ? `'What's New' text` : `release notes`}`;
        case ReleaseNotesDataSource.None:
            return "Don't apply automatically";
    }
};

export const displayTextForPromoTextDataSource: (dataSource: ReleaseNotesDataSource) => string = (dataSource) => {
    switch (dataSource) {
        case ReleaseNotesDataSource.PreviousRelease:
            return 'Pull in from previous release';
        case ReleaseNotesDataSource.DefaultReleaseNotes:
            return 'Use default promotional text';
        case ReleaseNotesDataSource.None:
            return "Don't apply automatically";
    }
};

export const displayTextForSyncTranslationsExportStrategy: (strategy: AutoSyncTranslationsExportStrategy) => string = (
    strategy: AutoSyncTranslationsExportStrategy,
) => {
    switch (strategy) {
        case AutoSyncTranslationsExportStrategy.ToWorkingBranch:
            return 'Working branch';
        case AutoSyncTranslationsExportStrategy.ToReleaseBranch:
            return 'Release branch';
    }
};

export const displayTextForAutoBackmergeStrategy: (strategy: AutoBackmergeStrategy) => string = (
    strategy: AutoBackmergeStrategy,
) => {
    switch (strategy) {
        case AutoBackmergeStrategy.OnReleaseCompletion:
            return 'Backmerge changes from the release branch all at once, at the end of each release cycle';
        case AutoBackmergeStrategy.Continuous:
            return 'Backmerge changes from the release branch continuously as they are merged';
        case AutoBackmergeStrategy.ContinuousCherryPick:
            return 'Cherry-pick changes from the release branch continuously as they are merged';
    }
};

export const displayTextForBumpVersionStrategy: (strategy: BumpVersionAutomationStrategy) => string = (
    strategy: BumpVersionAutomationStrategy,
) => {
    switch (strategy) {
        case BumpVersionAutomationStrategy.PreKickoffToCurrentVersionOnWorkingBranch:
            return 'Pre-kickoff, to current release version, on working branch';
        case BumpVersionAutomationStrategy.PostKickoffToCurrentVersionOnReleaseBranch:
            return 'Post-kickoff, to current release version, on release branch';
        case BumpVersionAutomationStrategy.PostReleaseToNextVersionOnWorkingBranch:
            return 'Post-release, to next version, on working branch';
        case BumpVersionAutomationStrategy.PostSubmitToNextVersionOnWorkingBranch:
            return 'Post-submission, to next version, on working branch';
        case BumpVersionAutomationStrategy.PostKickoffToNextVersionOnWorkingBranch:
            return 'Post-kickoff, to next version, on working branch';
    }
};

export const displayTextForAutomationReleaseType = (releaseType: AutomationReleaseTypes) => {
    switch (releaseType) {
        case AutomationReleaseTypes.All:
            return 'All types';
        case AutomationReleaseTypes.AllExceptHotfix:
            return 'All types except hotfixes';
        case AutomationReleaseTypes.HotfixOnly:
            return 'Hotfixes only';
    }
};

export const displayTextForReleaseTypeGroup = (group: ReleaseTypesForNonBlockingSteps): string => {
    switch (group) {
        case ReleaseTypesForNonBlockingSteps.Standard:
            return 'Standard releases';
        case ReleaseTypesForNonBlockingSteps.Hotfix:
            return 'Hotfixes';
        case ReleaseTypesForNonBlockingSteps.Rollback:
            return 'Rollbacks';
    }
};

export const displayTextForAndroidAutoRolloutStartStrategy: (
    strategy: AndroidAutoRolloutStartStrategy,
    storeType: AppStoreType,
) => string = (strategy: AndroidAutoRolloutStartStrategy, storeType: AppStoreType) => {
    switch (strategy) {
        case AndroidAutoRolloutStartStrategy.Manual:
            return 'Start manually';
        case AndroidAutoRolloutStartStrategy.Auto:
            return `Start automatically${storeType == AppStoreType.Google ? ' on submission' : ''}`;
        case AndroidAutoRolloutStartStrategy.AutoFromForwardedEmail:
            return 'Start automatically on release (requires app store email forwarding)';
    }
};

export const availableAndroidAutoRolloutStartStrategiesForStoreType: (
    store: AppStoreType,
) => AndroidAutoRolloutStartStrategy[] = (store: AppStoreType) => {
    switch (store) {
        case AppStoreType.Google:
            return AllAndroidRolloutStartStrategies;
        case AppStoreType.Huawei:
            return [AndroidAutoRolloutStartStrategy.Manual, AndroidAutoRolloutStartStrategy.Auto];
        case AppStoreType.Amazon:
            return [];
        default:
            return [];
    }
};

export const displayTextForReleaseScheduleCadenceStyle: (
    style: ReleaseScheduleCadenceStyle,
    isMultipleReleasesCadence?: boolean,
) => string = (style: ReleaseScheduleCadenceStyle, isMultipleReleasesCadence?: boolean) => {
    switch (style) {
        case ReleaseScheduleCadenceStyle.Biweekly:
            return 'Bi-weekly';
        case ReleaseScheduleCadenceStyle.Monthly:
            return 'Monthly';
        case ReleaseScheduleCadenceStyle.ThreeWeeks:
            return 'Every three weeks';
        case ReleaseScheduleCadenceStyle.SixWeeks:
            return 'Every six weeks';
        case ReleaseScheduleCadenceStyle.Weekly:
            return (
                isMultipleReleasesCadence === true ? 'Weekly (multiple per week)'
                : isMultipleReleasesCadence === false ? 'Weekly'
                : 'Weekly (or more)'
            );
        case ReleaseScheduleCadenceStyle.Never:
            return 'Not enabled';
    }
};

export const displayTextForLifecycleFreezeSets: (lifecycleFreezeSets: LifecycleFreezeSet[]) => string[][] = (
    lifecycleFreezeSets: LifecycleFreezeSet[],
) => {
    return lifecycleFreezeSets.map((dateSet) => {
        const startDate = formatDateString(dateSet.startDate, 'ddd, MMM D');
        const endDate = formatDateString(dateSet.endDate, 'ddd, MMM D');
        const events = dateSet.affectedEvents.map((event) => tooltipDisplayLifecycleEventName(event)).join(', ');
        return [`${startDate} - ${endDate}`, events];
    });
};

export const displayTextForDayOfTheWeek: (dayOfTheWeek: DayOfTheWeek) => string = (dayOfTheWeek: DayOfTheWeek) => {
    switch (dayOfTheWeek) {
        case DayOfTheWeek.Monday:
            return 'Monday';
        case DayOfTheWeek.Tuesday:
            return 'Tuesday';
        case DayOfTheWeek.Wednesday:
            return 'Wednesday';
        case DayOfTheWeek.Thursday:
            return 'Thursday';
        case DayOfTheWeek.Friday:
            return 'Friday';
        case DayOfTheWeek.Saturday:
            return 'Saturday';
        case DayOfTheWeek.Sunday:
            return 'Sunday';
    }
};

export const displayTextForReleaseMethod: (releaseMethod: ReleaseMethod) => string = (releaseMethod: ReleaseMethod) => {
    switch (releaseMethod) {
        case ReleaseMethod.Manual:
            return 'Manual - release updates manually';
        case ReleaseMethod.AutomaticOnApproval:
            return 'Automatic - release immediately upon approval';
        case ReleaseMethod.AutomaticOnDate:
            return 'Automatic, no earlier than release date and time';
    }
};

export const displayTextForChecklistItemStatusType: (status: ChecklistItemStatusType) => string = (status) => {
    switch (status) {
        case ChecklistItemStatusType.InProgress:
            return 'In progress';
        case ChecklistItemStatusType.Approved:
            return 'Approved';
        case ChecklistItemStatusType.Failed:
            return 'Failed';
        case ChecklistItemStatusType.Blocked:
            return 'Blocked';
        case ChecklistItemStatusType.Passed:
            return 'Passed';
        case ChecklistItemStatusType.Rejected:
            return 'Rejected';
    }
};

export const getTimelineIconForChecklistItemStatusType = (status: ChecklistItemStatusType) => {
    switch (status) {
        case ChecklistItemStatusType.Approved:
        case ChecklistItemStatusType.Passed:
            return CheckCircleOutlined;
        case ChecklistItemStatusType.InProgress:
            return ClockCircleOutlined;
        case ChecklistItemStatusType.Rejected:
        case ChecklistItemStatusType.Failed:
        case ChecklistItemStatusType.Blocked:
            return CloseCircleOutlined;
    }
};

export const getTimelineColorForChecklistItemStatusType = (status: ChecklistItemStatusType) => {
    switch (status) {
        case ChecklistItemStatusType.Approved:
        case ChecklistItemStatusType.Passed:
            return runwayColors.statusGreen;
        case ChecklistItemStatusType.InProgress:
            return runwayColors.statusYellow;
        case ChecklistItemStatusType.Rejected:
        case ChecklistItemStatusType.Failed:
        case ChecklistItemStatusType.Blocked:
            return runwayColors.statusRed;
    }
};

export const displayTextForForgotPasswordError: (error: ForgotPasswordError) => string = (error) => {
    switch (error) {
        case ForgotPasswordError.General:
            return 'Reset password failed. Please try again.';
        case ForgotPasswordError.CodeNotVerified:
            return 'Your reset password code is no longer valid. Please try again.';
        case ForgotPasswordError.Unauthorized:
            return 'Unauthorized.';
        default:
            return 'Reset password failed. Please try again.';
    }
};

export const displayCodeStatusForStatus: (
    status: FeatureItemCodeStatus | PullRequestMergeQueueState,
    useWorkingBranch: boolean,
    mergeQueueState?: PullRequestMergeQueueState,
    isDraft?: boolean,
) => string = (
    status: FeatureItemCodeStatus | PullRequestMergeQueueState,
    useWorkingBranch: boolean,
    mergeQueueState?: PullRequestMergeQueueState,
    isDraft?: boolean,
) => {
    if (status === PullRequestMergeQueueState.Queued || mergeQueueState === PullRequestMergeQueueState.Queued) {
        return 'In merge queue';
    }

    if (isDraft) {
        return 'Draft PR';
    }

    switch (status) {
        case FeatureItemCodeStatus.CherryPickPending:
            return 'Cherry-pick pending';
        case FeatureItemCodeStatus.Merged:
            return 'Merged';
        case FeatureItemCodeStatus.OpenPR:
            return 'Open PR';
        case FeatureItemCodeStatus.Unmerged:
            return useWorkingBranch ? 'Not on working branch' : 'Not on release branch';
        case FeatureItemCodeStatus.CherryPickFailed:
            return 'Cherry-pick failed';
        case FeatureItemCodeStatus.CherryPickOpen:
            return 'Cherry-pick PR open';
        case FeatureItemCodeStatus.CherryPickMerged:
            return 'Cherry-pick merged';
        case FeatureItemCodeStatus.CherryPickClosed:
            return 'Fix canceled';
        case FeatureItemCodeStatus.CherryPickSkipped:
            return 'Cherry-pick skipped';
        case FeatureItemCodeStatus.CherryPickManuallyResolved:
            return 'Cherry-pick resolved';
        case FeatureItemCodeStatus.CherryPickQueued:
            return 'Cherry-pick queued';
    }
};

export const displayTextForSortType: (sortType: SortType) => string = (sortType) => {
    switch (sortType) {
        case SortType.CodeAuthor:
            return 'Code author';
        case SortType.CodeLastUpdated:
            return 'Code last updated';
        case SortType.CodeStatus:
            return 'Code status';
        case SortType.LastUpdated:
            return 'Last updated';
        case SortType.TicketLastUpdated:
            return 'Ticket last updated';
        case SortType.TicketOwner:
            return 'Ticket owner';
        case SortType.TicketStatus:
            return 'Ticket status';
    }
};

export const displayTextForAutomationsToolboxSortType: (sortType: AutomationsToolboxSortType) => string = (
    sortType,
) => {
    switch (sortType) {
        case AutomationsToolboxSortType.Category:
            return 'Category';
        case AutomationsToolboxSortType.Enabled:
            return 'Enabled';
        case AutomationsToolboxSortType.New:
            return 'New';
    }
};

export const displayTextForFeatureFlagsSortType: (sortType: FeatureFlagsSortType) => string = (sortType) => {
    switch (sortType) {
        case FeatureFlagsSortType.Highlighted:
            return 'Highlighted';
        case FeatureFlagsSortType.Status:
            return 'Status';
        case FeatureFlagsSortType.CreatedOn:
            return 'Created on';
        case FeatureFlagsSortType.Owner:
            return 'Owner';
        case FeatureFlagsSortType.Name:
            return 'Name';
    }
};

export const displayTextForApprovalsSortType: (sortType: ApprovalsSortType) => string = (sortType) => {
    switch (sortType) {
        case ApprovalsSortType.ItemStatus:
            return 'Item Status';
        case ApprovalsSortType.Default:
            return 'Default';
    }
};

export const displayTextForSetupStepTypeIntegrations: (setupStepType: SetupStepType) => string = (setupStepType) => {
    switch (setupStepType) {
        case SetupStepType.BranchingStrategy:
            return `Runway works better when it’s connected to your version control system and a CI.\n\nSave time with your builds and branches setup by connecting those now.`;
        default:
            return '';
    }
};

export const capabilityNameWithPrefix: (capability: IntegrationCapability) => string = (capability) => {
    const name = displayNameForCapability(capability);
    switch (capability) {
        case IntegrationCapability.SDKDistribution:
        case IntegrationCapability.AppStore:
        case IntegrationCapability.ObservabilityAndAnalytics:
            return `an ${name}`;
        case IntegrationCapability.FeatureManagement:
        case IntegrationCapability.VersionControl:
        case IntegrationCapability.BetaTesting:
        case IntegrationCapability.Notifications:
        case IntegrationCapability.BuildSystem:
        case IntegrationCapability.RegressionTesting:
        case IntegrationCapability.StabilityMonitoring:
        case IntegrationCapability.FeatureFlagging:
        case IntegrationCapability.Translations:
        case IntegrationCapability.Scheduling:
        case IntegrationCapability.Calendar:
            return `a ${name}`;
    }
};

export const documentationLinkForCapability: (capability: IntegrationCapability) => string = (capability) => {
    switch (capability) {
        case IntegrationCapability.AppStore:
            return 'https://docs.runway.team/integrations/app-stores';
        case IntegrationCapability.FeatureManagement:
            return 'https://docs.runway.team/integrations/project-management';
        case IntegrationCapability.VersionControl:
            return 'https://docs.runway.team/integrations/version-control';
        case IntegrationCapability.BuildSystem:
            return 'https://docs.runway.team/integrations/ci-cd';
        case IntegrationCapability.Notifications:
            return 'https://docs.runway.team/integrations/notifications';
        case IntegrationCapability.StabilityMonitoring:
            return 'https://docs.runway.team/integrations/stability-monitoring';
        case IntegrationCapability.RegressionTesting:
            return 'https://docs.runway.team/integrations/regression-testing';
        case IntegrationCapability.BetaTesting:
            return 'https://docs.runway.team/using-runway/release-steps/beta-testing';
        case IntegrationCapability.FeatureFlagging:
            return 'https://docs.runway.team/using-runway/release-steps/feature-flags';
        case IntegrationCapability.Translations:
            return 'https://docs.runway.team/using-runway/release-steps/translations';
        default:
            return 'https://docs.runway.team/';
    }
};

export const displaySimplifiedNameForMonitoringMetricType: (type: AppMonitoringHealthMetricType) => string = (type) => {
    switch (type) {
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.MergedUserStability:
            return 'Crash-free rate';
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
            return 'Crash-free sessions';
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return 'User rating average';
        case StabilityMetricType.UserStability:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return 'Crash-free users';
        case StabilityMetricType.AdoptionRate:
            return 'Adoption rate';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
            return 'Events per DAU';
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
            return 'Events per session';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
            return 'Avg property values';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return 'Median property values';
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
            return '90th percentile';
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
            return '95th percentile';
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return '99th percentile';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
            return 'Value';
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return 'Calculated value';
    }
};

export const displayNameForMonitoringMetricType: (type: AppMonitoringHealthMetricType) => string = (type) => {
    switch (type) {
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.MergedUserStability:
            return 'Crash-free rate';
        case StabilityMetricType.SessionStability:
            return 'Crash-free sessions';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
            return 'Crash-free sessions delta from previous version as a percentage';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
            return 'Crash-free sessions delta from previous version ';
        case UserRatingsMetricType.AvgUserRating:
            return 'User rating average';
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
            return 'User rating average delta from previous version as a percentage';
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return 'User rating average delta from previous version';
        case StabilityMetricType.UserStability:
            return 'Crash-free users';
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
            return 'Crash-free users delta from previous version as a percentage';
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return 'Crash-free users delta from previous version';
        case StabilityMetricType.AdoptionRate:
            return 'Adoption rate';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
            return 'Events per DAU';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
            return 'Events per DAU delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
            return 'Events per DAU delta from previous version';
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
            return 'Events per session';
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
            return 'Events per session delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
            return 'Events per session delta from previous version';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
            return 'Avg property values';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
            return 'Avg of property value delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
            return 'Avg of property value delta from previous version';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
            return 'Median property values';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
            return 'Median of property value delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return 'Median of property value delta value from previous version';
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
            return '90th percentile';
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
            return '90th percentile of property value delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
            return '90th percentile of property value delta value from previous version';
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
            return '95th percentile';
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
            return '95th percentile of property value delta from previous version';
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
            return '95th percentile of property value delta value from previous version';
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
            return '99th percentile';
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
            return '99th percentile of property value delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return '99th percentile of property value delta value from previous version';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
            return 'Value';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
            return 'Delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
            return 'Delta from previous version';
        case ObservabilityAndAnalyticsMetricType.Composed:
            return 'Calculated value';
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
            return 'Calculated value delta from previous version as a percentage';
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return 'Calculated value delta from previous version';
    }
};

export const displayTextForMonitoringMetric: (
    metric: AppMonitoringHealthMetric,
    includeEventUnits?: boolean,
) => string = (metric, includeEventUnits = false) => {
    if (metric.customDisplayName) {
        return metric.customDisplayName;
    }
    switch (metric.type) {
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
        case StabilityMetricType.MergedSessionStability:
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
        case StabilityMetricType.UserStability:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
        case StabilityMetricType.MergedUserStability:
        case StabilityMetricType.AdoptionRate:
            return displayNameForMonitoringMetricType(metric.type);
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue: {
            const obaaMetric = metric as AppMonitoringObservabilityAndAnalyticsHealthMetric;
            const base =
                obaaMetric.displayName ??
                `${obaaMetric.eventName}` + (obaaMetric.paramName ? ` (${obaaMetric.paramName})` : '');
            const units =
                includeEventUnits ?
                    ` (${displayObservabilityAndAnalyticsMetricsTypeAsAverage(metric.type, metric.integrationId, true)})`
                :   null;

            return `${base}${units ?? ''}`;
        }
    }
};

// e.g. "Crash-free users > 99.0%", "View app home event (events per DAU) > 3.4"
export const fullDisplayTextForMonitoringMetric: (metric: AppMonitoringHealthMetric) => string | null = (metric) => {
    if (metric?.thresholds.length === 0) {
        return null;
    }

    const threshold = metric.thresholds[0];

    switch (metric.type) {
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.UserStability:
        case StabilityMetricType.MergedUserStability:
        case UserRatingsMetricType.AvgUserRating:
            return `${displayTextForMonitoringMetric(metric)} ${symbolForMetricConditionOperator(
                threshold.condition,
            )} ${displayFormattedMonitoringMetricValue(metric.type, threshold.value)}`;
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
        case StabilityMetricType.AdoptionRate:
            return `${displayTextForMonitoringMetric(metric)} ${symbolForMetricConditionOperator(
                threshold.condition,
            )} ${displayFormattedMonitoringMetricValueWithSign(metric.type, threshold.value)}`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.Composed:
            return `${displayTextForMonitoringMetric(metric, true)} ${symbolForMetricConditionOperator(
                threshold.condition,
            )} ${displayFormattedMonitoringMetricValue(metric.type, threshold.value)}`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return `${displayTextForMonitoringMetric(metric, true)} ${symbolForMetricConditionOperator(
                threshold.condition,
            )} ${displayFormattedMonitoringMetricValueWithSign(metric.type, threshold.value)}`;
    }
};

export const displayThresholdForMonitoringStatisticGroup = (group: MonitoringStatisticGroup): string => {
    if (group.threshold === undefined || group.thresholdCondition === undefined) {
        return 'No threshold';
    }

    const forcedUnit =
        isPercentageMonitoringMetric(group.type, group.integrationType, group.eventName) ?
            StatisticUnit.Percent
        :   undefined;

    return `${symbolForMetricConditionOperator(group.thresholdCondition)} ${displayFormattedMonitoringMetricValue(
        group.type,
        group.threshold,
        forcedUnit,
    )}`;
};

export const displayFormattedMonitoringMetricValueWithSign: (
    type: AppMonitoringHealthMetricType,
    value: number,
    forcedUnit?: StatisticUnit,
) => string | null = (type, value, forcedUnit) => {
    const statisticType = statTypeForMonitoringMetricType(type);

    if (isNil(value) || Number.isNaN(value)) {
        return null;
    }

    const unit = forcedUnit ?? unitForMonitoringMetricType(type);

    return displayStatValueWithSign(statisticType, value, unit);
};

export const displayFormattedMonitoringMetricValue: (
    type: AppMonitoringHealthMetricType,
    value: number,
    forcedUnit?: StatisticUnit,
) => string | null = (type, value, forcedUnit) => {
    const statisticType = statTypeForMonitoringMetricType(type);

    if (isNil(value) || Number.isNaN(value)) {
        return null;
    }

    const unit = forcedUnit ?? unitForMonitoringMetricType(type);

    return displayStatValue(statisticType, value, unit);
};

export const displaySectionTitleForMonitoringCategory: (category: AppMonitoringHealthMetricCategory) => string = (
    category,
) => {
    switch (category) {
        case AppMonitoringHealthMetricCategory.UserReviews:
            return 'Ratings';
        case AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics:
            return 'Observability & analytics';
        case AppMonitoringHealthMetricCategory.Stability:
            return 'Stability monitoring';
    }
};

export const displayTextForAutomationConditionType: (type: MonitoringAutomationConditionType) => string = (type) => {
    switch (type) {
        case MonitoringAutomationConditionType.Adoption:
            return 'Release adoption';
        case MonitoringAutomationConditionType.PhasedReleaseDay:
            return 'Phased release progress';
        case MonitoringAutomationConditionType.StagedRolloutPercentage:
            return 'Staged rollout percentage';
    }
};

export const rolloutNounForAppStoreType = (appStoreType?: AppStoreType): string =>
    appStoreType === AppStoreType.Apple ? 'phased release' : 'staged rollout';

export const displayTextForNotificationTriggerConditionType: (
    type: NotificationTriggerConditionType,
    appStoreType?: AppStoreType,
) => string = (type, appStoreType) => {
    const rolloutNoun = rolloutNounForAppStoreType(appStoreType);
    switch (type) {
        case NotificationTriggerConditionType.RolloutDay:
            return capitalize(`${rolloutNoun} day`);
        case NotificationTriggerConditionType.RolloutPercentage:
            return capitalize(`${rolloutNoun} percentage`);
        case NotificationTriggerConditionType.PreviousReleaseFullyRolledOut:
            return 'Previous release reached 100%';
    }
};

export const displayObservabilityAndAnalyticsMetricsTypeAsAverage = (
    type: ObservabilityAndAnalyticsMetricType,
    integrationType?: IntegrationType,
    short?: boolean,
) => {
    const [propertySingular] = integrationType ? eventPropertyTerminology(integrationType) : ['property'];
    switch (type) {
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
            return short ? 'events per DAU' : 'Events per DAU';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
            return short ? 'events per DAU (delta %)' : 'Events per DAU (delta as percentage)';
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
            return short ? 'events per DAU (delta)' : 'Events per DAU (delta from prev version)';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
            return short ? `avg of ${propertySingular} value` : `Average ${propertySingular} value`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
            return short ?
                    `avg of ${propertySingular} value (delta %)`
                :   `Average ${propertySingular} value (delta as percentage)`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
            return short ?
                    `avg of ${propertySingular} value (delta)`
                :   `Average ${propertySingular} value (delta from prev version)`;
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
            return short ? 'events per session' : 'Events per session';
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
            return short ? 'events per session (delta %)' : 'Events per session (delta as percentage)';
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
            return short ? 'events per session (delta)' : 'Events per session (delta from prev version)';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
            return short ? `median of ${propertySingular} value` : `Median of ${propertySingular} value`;
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
            return short ?
                    `median of ${propertySingular} value (delta %)`
                :   `Median of ${propertySingular} value (delta as percentage)`;
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return short ?
                    `median of ${propertySingular} value (delta)`
                :   `Median of ${propertySingular} value (delta from prev version)`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
            return short ? `90p of ${propertySingular} value` : `90th percentile of ${propertySingular} value`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
            return short ?
                    `90p of ${propertySingular} value (delta %)`
                :   `90th percentile of ${propertySingular} value (delta as percentage)`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
            return short ?
                    `90p of ${propertySingular} value (delta)`
                :   `90th percentile of ${propertySingular} value (delta from prev version)`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
            return short ? `95p of ${propertySingular} value` : `95th percentile of ${propertySingular} value`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
            return short ?
                    `95p of ${propertySingular} value (delta %)`
                :   `95th percentile of ${propertySingular} value (delta as percentage)`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
            return short ?
                    `95p of ${propertySingular} value (delta)`
                :   `95th percentile of ${propertySingular} value (delta from prev version)`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
            return short ? `99p of ${propertySingular} value` : `99th percentile of ${propertySingular} value`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
            return short ?
                    `99p of ${propertySingular} value (delta %)`
                :   `99th percentile of ${propertySingular} value (delta as percentage)`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return short ?
                    `99p of ${propertySingular} value (delta)`
                :   `99th percentile of ${propertySingular} value (delta from prev version)`;
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
            return short ? 'value' : 'Value';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
            return short ? 'value (delta %)' : 'Value (delta as percentage)';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
            return short ? 'value (delta)' : 'Value (delta from prev version)';
        case ObservabilityAndAnalyticsMetricType.Composed:
            return short ? 'calculated value' : 'Calculated value';
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
            return short ? 'calculated value (delta %)' : 'Calculated value (delta as percentage)';
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return short ? 'calculated value (delta)' : 'Calculated value (delta from prev version)';
    }
};

export const colorForMonitoringMetricHealthStatus = (status?: MonitoringMetricHealthStatus) => {
    switch (status) {
        case MonitoringMetricHealthStatus.Healthy:
            return runwayColors.statusGreen;
        case MonitoringMetricHealthStatus.Unhealthy:
            return runwayColors.statusRed;
        default:
            return antGrey.grey5;
    }
};

export const phasedReleasePausedDisplayTextForAppStoreType: (appStoreType: AppStoreType) => string = (appStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Google:
            return 'Halted';
        case AppStoreType.Apple:
        case AppStoreType.Huawei:
            return 'Paused';
        default:
            return '';
    }
};

export const phasedReleaseDisplayTextForAppStoreType: (appStoreType: AppStoreType) => string = (appStoreType) => {
    switch (appStoreType) {
        case AppStoreType.Google:
            return 'staged rollout';
        case AppStoreType.Apple:
        case AppStoreType.Huawei:
            return 'phased release';
        default:
            return '';
    }
};

export const displayTextForPhasedReleaseState: (
    state: PhasedReleaseState,
    isManuallyAccelerated: boolean,
    isAutoAccelerated: boolean,
    isAutoHalted: boolean,
    isSuperseded?: boolean,
    isRolloutComplete?: boolean,
) => string = (state, isManuallyAccelerated, isAutoAccelerated, isAutoHalted, isSuperseded, isRolloutComplete) => {
    if (isSuperseded) {
        return 'Superseded';
    }

    const completedText =
        isAutoAccelerated ? 'Accelerated by Runway'
        : isManuallyAccelerated ? 'Manually accelerated'
        : 'Completed';

    switch (state) {
        case GooglePlayStoreState.Halted:
            return isAutoHalted ? 'Halted by Runway' : 'Halted';
        case AppleAppStorePhasedReleaseState.Paused:
        case HuaweiPhasedReleaseState.Suspended:
            return isAutoHalted ? 'Paused by Runway' : 'Paused';
        case GooglePlayStoreState.InProgress:
        case ExpoEASRolloutState.InProgress:
            return 'In progress';
        case AppleAppStorePhasedReleaseState.Active:
            return 'Active';
        case HuaweiPhasedReleaseState.Released:
            return isRolloutComplete ? completedText : 'In progress';
        case AppleAppStorePhasedReleaseState.Complete:
        case GooglePlayStoreState.Completed:
        case ExpoEASRolloutState.Complete:
            return completedText;
        case HuaweiPhasedReleaseState.Canceled:
            return 'Canceled';
        case GooglePlayStoreState.Draft:
        case GooglePlayStoreState.StatusUnspecified:
        case HuaweiPhasedReleaseState.Draft:
            return '';
        case AppleAppStorePhasedReleaseState.Inactive:
            return 'Inactive';
        default:
            return '';
    }
};

export const displayTextForPausedOrAcceleratedPhasedReleaseGroup: (
    groupRolloutsArePaused: boolean,
    rolloutsPausedByRunway: boolean,
    groupRolloutsAccelerated: boolean,
    rolloutsAcceleratedByRunway: boolean,
) => string = (
    groupRolloutsArePaused,
    rolloutsPausedByRunway,
    groupRolloutsAccelerated,
    rolloutsAcceleratedByRunway,
) => {
    if (groupRolloutsArePaused) {
        return rolloutsPausedByRunway ? 'Paused by Runway' : 'Paused';
    } else if (groupRolloutsAccelerated) {
        return rolloutsAcceleratedByRunway ? 'Accelerated by Runway' : 'Accelerated';
    } else {
        return '';
    }
};

export const displayTextForCategory: (category: AutomationCategory | AppNotificationCategory) => string = (
    category,
) => {
    return category.toString();
};

export const iconForCategory: (category: AutomationCategory) => React.ComponentType = (capability) => {
    switch (capability) {
        case AutomationCategory.ReleaseCycle:
            return ClockCircleOutlined;
        case AutomationCategory.AppStoreTasks:
            return AppstoreAddOutlined;
        case AutomationCategory.BetaTesting:
            return SafetyCertificateOutlined;
        case AutomationCategory.ReleaseHygiene:
            return ClearOutlined;
        case AutomationCategory.Triage:
            return InboxOutlined;
        case AutomationCategory.General:
            return PlayCircleOutlined;
        case AutomationCategory.Unknown:
            return WarningOutlined;
    }
};

export const displaySubscriptionStatus: (status: SubscriptionStatus, includeSuffix: boolean) => string = (
    status,
    includeSuffix,
) => {
    var plan: string;
    var suffix: string;
    switch (status) {
        case SubscriptionStatus.Free:
            return 'Free';
        case SubscriptionStatus.Paywall:
            return ''; // Not needed
        case SubscriptionStatus.CustomerTeam:
            plan = 'Team';
            suffix = 'Release Management';
            break;
        case SubscriptionStatus.CustomerBusiness:
        case SubscriptionStatus.CustomerEnterprise:
            plan = 'Enterprise';
            suffix = 'Release Management';
            break;
        case SubscriptionStatus.CustomerTeamBD:
            plan = 'Team';
            suffix = 'Build Distro';
            break;
        case SubscriptionStatus.CustomerBusinessBD:
        case SubscriptionStatus.CustomerEnterpriseBD:
            plan = 'Enterprise';
            suffix = 'Build Distro';
            break;
    }

    let str = plan;
    if (includeSuffix) {
        str += ' - ' + suffix;
    }
    return str;
};

export const featureFlagStatusDisplayString: (status: FeatureFlagStatus) => string = (status) => {
    switch (status) {
        case FeatureFlagStatus.Enabled:
            return 'enabled';
        case FeatureFlagStatus.Disabled:
        case FeatureFlagStatus.Paused:
        case FeatureFlagStatus.Archived:
            return 'disabled';
        default:
            return '';
    }
};

export const displayCustomMessageTitle: (key: CustomMessageConfigKey) => string = (status) => {
    switch (status) {
        case CustomMessageConfigKey.BumpVersionCommitMessage:
            return 'Bump version commit message';
        case CustomMessageConfigKey.BumpVersionPRTitle:
            return 'Bump version PR title';
        case CustomMessageConfigKey.BumpVersionPRBody:
            return 'Bump version PR body';
        case CustomMessageConfigKey.PromoteCodePRTitle:
            return 'Promote code PR title';
        case CustomMessageConfigKey.PromoteCodePRBody:
            return 'Promote code PR body';
        case CustomMessageConfigKey.BackMergeBranchPRTitle:
            return 'End of release backmerge PR title';
        case CustomMessageConfigKey.BackMergeBranchPRBody:
            return 'End of release backmerge PR body';
        case CustomMessageConfigKey.VCSReleaseTitle:
            return 'VCS release title';
        case CustomMessageConfigKey.VCSReleaseBody:
            return 'VCS release body';
        case CustomMessageConfigKey.TagMessage:
            return 'Release tag message';
        case CustomMessageConfigKey.CherryPickPRTitle:
            return 'Cherry-pick PR title';
        case CustomMessageConfigKey.CherryPickPRBody:
            return 'Cherry-pick PR body';
        case CustomMessageConfigKey.CherryPickCommitMessage:
            return 'Cherry-pick commit messages';
        case CustomMessageConfigKey.BackMergePRCommitPRBody:
            return 'Continuous backmerge PR body';
        case CustomMessageConfigKey.BackMergePRCommitPRTitle:
            return 'Continuous backmerge PR title';
        case CustomMessageConfigKey.ReleaseSummaryMessage:
            return 'Release summary message';
    }
};

export const descriptionForSetting = (
    setting: SettingType,
    appStoreType: AppStoreType,
    appName?: string,
    platform?: AppPlatform,
    platformLabelOverride?: string,
) => {
    const appAndPlatform =
        appName && platform ?
            `${appName} (${platformLabelOverride ?? displayTextForAppPlatform(platform, appStoreType)})`
        :   'this app';
    switch (setting) {
        case SettingType.General:
            return 'Manage your app settings';
        case SettingType.ReleasePilotRotation:
            return `View and configure your release pilot rotation for ${appAndPlatform}`;
        case SettingType.CustomStrings:
            return "Customize strings used by Runway when creating artifacts throughout the release's lifecycle";
        case SettingType.FixRequests:
            return 'Configure settings for work item Fixes that can be found in the Feature Readiness step';
        case SettingType.Triage:
            return `Configure how Triage detects issues for ${appAndPlatform}`;
        case SettingType.Integrations:
            return `Third-party connections and settings for ${appAndPlatform}`;
        case SettingType.Automations:
            return 'Manage automations that allow Runway to perform actions for you';
        case SettingType.Schedule:
            return 'Manage your release cadence and target dates';
        case SettingType.Checklists:
            return 'Manage checklist items for your team’s unique release process';
        case SettingType.Notifications:
            return 'Manage how Runway notifies your team about key events';
        case SettingType.HealthMetrics:
            return 'Configure key health metrics for monitoring the status of releases as they go live';
        case SettingType.ProfilesAndDevices:
            return 'Manage required test devices and profiles for this app';
        case SettingType.SigningKeys:
            return 'Manage the signing keys used for this app';
        case SettingType.AppStore:
            return `Manage default app store settings for ${appAndPlatform}`;
        case SettingType.Team:
            return `Manage your ${appAndPlatform} app's users`;
        case SettingType.DefaultReleaseNotes:
            return `Release notes will be saved to ${displayConsoleNameWithPrefix(
                appStoreType,
            )} by Runway. Notes will still
            need be manually approved for release.`;
        case SettingType.ScheduleCadence:
            return 'Configure your schedule cadence settings to let Runway automatically set target dates on upcoming releases. You can make always changes to target dates set on releases as needed.';
        case SettingType.WorkflowManagement:
            return 'Add, reorder and configure the steps and destinations in your flightpath';
        case SettingType.SetupTasks:
        case SettingType.LegacyWorkflowManagement:
        case SettingType.LegacyDestinations:
        case SettingType.LegacyWorkflowSteps:
            return null;
    }
};

export const displayNameForSetting = (setting: SettingType) => {
    switch (setting) {
        case SettingType.General:
            return 'App settings';
        case SettingType.ReleasePilotRotation:
            return 'Release pilot rotation';
        case SettingType.CustomStrings:
            return 'Custom strings';
        case SettingType.FixRequests:
            return 'Fix requests';
        case SettingType.Triage:
            return 'Triage';
        case SettingType.Integrations:
            return 'Integrations';
        case SettingType.Automations:
            return 'Automations';
        case SettingType.Schedule:
            return 'Schedule';
        case SettingType.Checklists:
            return 'Checklists';
        case SettingType.Notifications:
            return 'Notifications';
        case SettingType.HealthMetrics:
            return 'Health metrics';
        case SettingType.AppStore:
            return 'App store defaults';
        case SettingType.SetupTasks:
            return 'Setup tasks';
        case SettingType.Team:
            return 'Team';
        case SettingType.ProfilesAndDevices:
            return 'Profiles and devices';
        case SettingType.SigningKeys:
            return 'Signing keys';
        case SettingType.DefaultReleaseNotes:
            return 'Default release notes';
        case SettingType.ScheduleCadence:
            return 'Schedule cadence settings';
        case SettingType.WorkflowManagement:
            return 'Flightpath';
        case SettingType.LegacyWorkflowManagement:
        case SettingType.LegacyDestinations:
        case SettingType.LegacyWorkflowSteps:
            return '';
    }
};

// Converts the userFraction, which is a number from 0 to 1, to a number from 0 to 100 with two decimal places. Ex. 0.34856 would become 34.86
export const convertUserFraction: (userFraction: number) => number = (userFraction) => {
    return Math.round(userFraction * 10000) / 100;
};

// Note userFraction can have up to 6 decimal points
export const convertAndroidStagedRolloutPercentage: (userFraction: number) => number = (userFraction) => {
    return userFraction * 100;
};

export const displayStringForRolloutPercentage: (rolloutPercentage: number) => string = (rolloutPercentage) => {
    const truncatedRolloutPercentage = Number(rolloutPercentage.toFixed(6));

    return isNaN(rolloutPercentage) ? '--' : `${truncatedRolloutPercentage}%`;
};

export const gradleTaskForOutputType = (outputType: AndroidOutputType) => {
    switch (outputType) {
        case AndroidOutputType.AAB:
            return 'bundle';
        case AndroidOutputType.APK:
            return 'assemble';
    }
};

export const formatNameForCertificateType = (certificateType: string) => {
    const splitCertificateType = certificateType.split('_');
    return splitCertificateType.join(' ');
};

export const displayNameForAppleCertificateType: (type: AppleCertificateType) => string = (type) => {
    switch (type) {
        case AppleCertificateType.Development:
        case AppleCertificateType.MacAppDevelopment:
        case AppleCertificateType.IosDevelopment:
            return 'Development';
        case AppleCertificateType.Distribution:
        case AppleCertificateType.MacAppDistribution:
        case AppleCertificateType.IosDistribution:
            return 'Distribution';
        default:
            return 'Other';
    }
};

export const projectNameForPlatform: (platform: AppPlatform) => string = (platform) => {
    switch (platform) {
        case AppPlatform.Ios:
            return 'Xcode';
        case AppPlatform.Android:
            return 'Android';
        default:
            return '';
    }
};

export const identifierForAppPlatform: (platform: AppPlatform) => string = (platform) => {
    switch (platform) {
        case AppPlatform.TvOS:
        case AppPlatform.Ios:
        case AppPlatform.VisionOS:
            return 'bundle identifier';
        case AppPlatform.WearOS:
        case AppPlatform.Android:
            return 'package name';
        default:
            return '';
    }
};

export const artifactTypeDescription = (artifactType: BuildArtifactType) => {
    switch (artifactType) {
        case BuildArtifactType.AndroidDebuggableSigned:
            return 'Signed - Debuggable';
        case BuildArtifactType.AndroidReleaseSigned:
            return 'Signed - Release';
        case BuildArtifactType.AndroidDebuggableUnsigned:
            return 'Unsigned - Debuggable';
        case BuildArtifactType.AndroidReleaseUnsigned:
            return 'Unsigned - Release';
        case BuildArtifactType.TvOSAdHoc:
            return 'tvOS Ad Hoc';
        case BuildArtifactType.TvOSDevelopment:
            return 'tvOS Development';
        case BuildArtifactType.TvOSAppStore:
            return 'tvOS App Store';
        case BuildArtifactType.TvOSEnterprise:
            return 'tvOS Enterprise';
        case BuildArtifactType.IOSAdHoc:
            return 'Ad Hoc';
        case BuildArtifactType.IOSAppStore:
            return 'App Store';
        case BuildArtifactType.IOSDevelopment:
            return 'Development';
        case BuildArtifactType.IOSEnterprise:
            return 'Enterprise';
        case BuildArtifactType.AndroidAAB:
        case BuildArtifactType.AndroidAABInstallable:
            return 'Android App Bundle (AAB)';
    }
};

export const isDownloadOnlyBuild = (build?: BuildDetails): boolean =>
    !!build &&
    !build.binaryBuild &&
    !!build.downloadUrl &&
    build.artifactInfo?.downloadStatus === BuildDistroArtifactDownloadStatus.Success;

export const helperTextForArtifactType = (artifactType: BuildArtifactType, provisioningProfileName?: string) => {
    switch (artifactType) {
        case BuildArtifactType.AndroidDebuggableSigned:
            return 'This artifact is a signed Debug build, and can be installed by anyone.';
        case BuildArtifactType.AndroidReleaseSigned:
            return 'This artifact is a signed Release build, and can be installed by anyone.';
        case BuildArtifactType.AndroidDebuggableUnsigned:
            return 'This artifact is an unsigned Debug build, and cannot be installed on a device.';
        case BuildArtifactType.AndroidReleaseUnsigned:
            return 'This artifact is an unsigned Release build, and cannot be installed on a device.';
        case BuildArtifactType.TvOSAdHoc:
        case BuildArtifactType.IOSAdHoc:
            if (provisioningProfileName) {
                return `This artifact has an Ad Hoc profile, and can be installed by devices that are included in the provisioning profile:\n${provisioningProfileName}`;
            } else {
                return 'This artifact has an Ad Hoc profile, and can be installed by devices that are included in the corresponding provisioning profile.';
            }
        case BuildArtifactType.TvOSAppStore:
        case BuildArtifactType.IOSAppStore:
            return 'This artifact has an App Store profile, and cannot be installed directly.';
        case BuildArtifactType.TvOSDevelopment:
        case BuildArtifactType.IOSDevelopment:
            if (provisioningProfileName) {
                return `This artifact has a Development profile, and can be installed by devices that are included in the provisioning profile:\n${provisioningProfileName}`;
            } else {
                return 'This artifact has a Development profile, and can be installed by devices that are included in the corresponding provisioning profile.';
            }
        case BuildArtifactType.TvOSEnterprise:
        case BuildArtifactType.IOSEnterprise:
            return 'This artifact has an In House (Enterprise) profile, and can be installed by anyone.';
        case BuildArtifactType.AndroidAAB:
            return "This artifact is an Android App Bundle (AAB). AABs cannot be directly installed. Runway can generate installable APKs from AAB files but to do so, we need you to upload a signing key in the 'Signing keys' section of your app's settings.";
        case BuildArtifactType.AndroidAABInstallable:
            return 'This artifact is an Android App Bundle (AAB). A universal APK has been generated from this artifact by Runway.';
    }
};

export const shouldShowRegisterDevicesPromptForArtifactType = (artifactType: BuildArtifactType) => {
    switch (artifactType) {
        case BuildArtifactType.AndroidDebuggableSigned:
        case BuildArtifactType.AndroidReleaseSigned:
        case BuildArtifactType.AndroidDebuggableUnsigned:
        case BuildArtifactType.AndroidReleaseUnsigned:
        case BuildArtifactType.TvOSAppStore:
        case BuildArtifactType.IOSAppStore:
        case BuildArtifactType.TvOSEnterprise:
        case BuildArtifactType.IOSEnterprise:
        case BuildArtifactType.AndroidAAB:
        case BuildArtifactType.AndroidAABInstallable:
            return false;
        case BuildArtifactType.IOSAdHoc:
        case BuildArtifactType.TvOSAdHoc:
        case BuildArtifactType.TvOSDevelopment:
        case BuildArtifactType.IOSDevelopment:
            return true;
    }
};

export const displayTextForProvisioningProfileType = (profileType: ProvisioningProfileType) => {
    switch (profileType) {
        case ProvisioningProfileType.CatalystDevelopment:
        case ProvisioningProfileType.TvosDevelopment:
        case ProvisioningProfileType.MacDevelopment:
        case ProvisioningProfileType.IosDevelopment:
            return 'Development';
        case ProvisioningProfileType.CatalyistAppStore:
        case ProvisioningProfileType.TvosAppStore:
        case ProvisioningProfileType.MacAppStore:
        case ProvisioningProfileType.IosAppStore:
            return 'App Store';
        case ProvisioningProfileType.TvosAdHoc:
        case ProvisioningProfileType.IosAdHoc:
            return 'Ad Hoc';
        case ProvisioningProfileType.TvosInHouse:
        case ProvisioningProfileType.IosInHouse:
            return 'In House';
        case ProvisioningProfileType.CatalystDirect:
        case ProvisioningProfileType.MacDirect:
            return 'Direct';
    }
};

export const displayTextForProvisioningProfileTypeWithPlatform = (profileType: ProvisioningProfileType) => {
    switch (profileType) {
        case ProvisioningProfileType.CatalystDevelopment:
            return 'Catalyst Development';
        case ProvisioningProfileType.TvosDevelopment:
            return 'tvOS Development';
        case ProvisioningProfileType.MacDevelopment:
            return 'macOS Development';
        case ProvisioningProfileType.IosDevelopment:
            return 'iOS Development';
        case ProvisioningProfileType.CatalyistAppStore:
            return 'Catalyst App Store';
        case ProvisioningProfileType.TvosAppStore:
            return 'tvOS App Store';
        case ProvisioningProfileType.MacAppStore:
            return 'macOS App Store';
        case ProvisioningProfileType.IosAppStore:
            return 'iOS App Store';
        case ProvisioningProfileType.TvosAdHoc:
            return 'tvOS Ad Hoc';
        case ProvisioningProfileType.IosAdHoc:
            return 'iOS Ad Hoc';
        case ProvisioningProfileType.TvosInHouse:
            return 'tvOS In House';
        case ProvisioningProfileType.IosInHouse:
            return 'iOS In House';
        case ProvisioningProfileType.CatalystDirect:
            return 'Catalyst Direct';
        case ProvisioningProfileType.MacDirect:
            return 'macOS Direct';
    }
};

export const getDisplayableWorkflowNames: (params: {
    buildSystemIntegration: Integration;
    provider: IntegrationProvider;
    shortString?: boolean;
}) => {
    workingBranchWorkflow?: string;
    RCWorkflow?: string;
    productionWorkflow?: string;
} = ({ buildSystemIntegration, provider, shortString = false }) => {
    let workingBranchWorkflow: string | undefined;
    let RCWorkflow: string | undefined;
    let productionWorkflow: string | undefined;

    if (!buildSystemIntegration || !provider) {
        return {
            workingBranchWorkflow,
            RCWorkflow,
            productionWorkflow,
        };
    }
    switch (buildSystemIntegration.id) {
        case IntegrationType.Codemagic: {
            const { workflowName, devWorkflowName, prodWorkflowName, appId, devAppId, prodAppId } =
                buildSystemIntegration.data;

            const appName = (provider.data as CodemagicProviderData)?.availableApps?.find(
                (app) => app.id === appId,
            )?.appName;

            const devAppName = (provider.data as CodemagicProviderData)?.availableApps?.find(
                (app) => app.id === devAppId,
            )?.appName;

            const prodAppName = (provider.data as CodemagicProviderData)?.availableApps?.find(
                (app) => app.id === prodAppId,
            )?.appName;

            if (workflowName) {
                RCWorkflow = `${appName}/${workflowName}`;
            }
            if (devWorkflowName) {
                workingBranchWorkflow = `${devAppName}/${devWorkflowName}`;
            }
            if (prodWorkflowName) {
                productionWorkflow = `${prodAppName}/${prodWorkflowName}`;
            }
            break;
        }
        case IntegrationType.Bitrise: {
            const { workflowId, devWorkflowId, prodWorkflowId, appSlug, devAppSlug, prodAppSlug } =
                buildSystemIntegration.data;

            const appTitle = (provider.data as BitriseProviderData)?.availableApps?.find(
                (app) => app.slug === appSlug,
            )?.title;

            const devAppTitle = (provider.data as BitriseProviderData)?.availableApps?.find(
                (app) => app.slug === devAppSlug,
            )?.title;

            const prodAppTitle = (provider.data as BitriseProviderData)?.availableApps?.find(
                (app) => app.slug === prodAppSlug,
            )?.title;

            if (workflowId) {
                RCWorkflow = `${appTitle}/${workflowId}`;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = `${devAppTitle}/${devWorkflowId}`;
            }
            if (prodWorkflowId) {
                productionWorkflow = `${prodAppTitle}/${prodWorkflowId}`;
            }
            break;
        }
        case IntegrationType.Buildkite: {
            const { workflowId, devWorkflowId, prodWorkflowId, org } = buildSystemIntegration.data;

            const slugTitle = (provider.data as BuildkiteProviderData)?.availableOrgsAndPipelines?.find(
                (orgAndPipelines) => orgAndPipelines.org.id === org?.id,
            )?.org.name;

            if (workflowId) {
                RCWorkflow = `${slugTitle}/${workflowId}`;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = `${slugTitle}/${devWorkflowId}`;
            }
            if (prodWorkflowId) {
                productionWorkflow = `${slugTitle}/${prodWorkflowId}`;
            }
            break;
        }
        case IntegrationType.AzureCI: {
            const { workflowId, devWorkflowId, prodWorkflowId, selectedProject } = buildSystemIntegration.data;

            const projectTitle = (provider.data as AzureProviderData)?.availableProjectsAndResources?.find(
                (projectAndResources) => projectAndResources.project.id === selectedProject?.id,
            )?.project.name;

            if (workflowId) {
                RCWorkflow = `${projectTitle}/${workflowId}`;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = `${projectTitle}/${devWorkflowId}`;
            }
            if (prodWorkflowId) {
                productionWorkflow = `${projectTitle}/${prodWorkflowId}`;
            }
            break;
        }
        case IntegrationType.CircleCI: {
            const { workflowId, devWorkflowId, prodWorkflowId } = buildSystemIntegration.data;
            if (workflowId) {
                RCWorkflow = workflowId;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = devWorkflowId;
            }
            if (prodWorkflowId) {
                productionWorkflow = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.BitbucketCI: {
            const { workflowId, devWorkflowId, prodWorkflowId } = buildSystemIntegration.data;
            if (workflowId) {
                RCWorkflow = workflowId;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = devWorkflowId;
            }
            if (prodWorkflowId) {
                productionWorkflow = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.GenericCI: {
            const { workflowId, devWorkflowId, prodWorkflowId } = buildSystemIntegration.data;
            if (workflowId) {
                RCWorkflow = workflowId;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = devWorkflowId;
            }
            if (prodWorkflowId) {
                productionWorkflow = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.GitHubCI: {
            const { repoName, workflowId, devWorkflowId, prodWorkflowId } = buildSystemIntegration.data;

            const getName = (workflowId: string) => {
                return repoName ?
                        (provider.data as GitHubProviderData)?.availableWorkflows[repoName]?.find(
                            (workflow) => workflow.id === workflowId,
                        )?.name
                    :   undefined;
            };

            if (workflowId) {
                const name = getName(workflowId) ?? '';
                RCWorkflow = `${repoName}/${name}`;
            }
            if (devWorkflowId) {
                const name = getName(devWorkflowId) ?? '';
                workingBranchWorkflow = `${repoName}/${name}`;
            }
            if (prodWorkflowId) {
                const name = getName(prodWorkflowId) ?? '';
                productionWorkflow = `${repoName}/${name}`;
            }
            break;
        }
        case IntegrationType.GitLabCI: {
            const { workflowId, devWorkflowId, prodWorkflowId } = buildSystemIntegration.data ?? {};
            if (workflowId) {
                RCWorkflow = workflowId;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = devWorkflowId;
            }
            if (prodWorkflowId) {
                productionWorkflow = prodWorkflowId;
            }
            break;
        }
        case IntegrationType.Jenkins: {
            const { workflowId, devWorkflowId, prodWorkflowId, subJobName, prodSubJobName, devSubJobName } =
                buildSystemIntegration.data;
            if (workflowId) {
                RCWorkflow = workflowId;
                if (subJobName) {
                    RCWorkflow += `/${subJobName}`;
                }
            }
            if (devWorkflowId) {
                workingBranchWorkflow = devWorkflowId;
                if (devSubJobName) {
                    workingBranchWorkflow += `/${devSubJobName}`;
                }
            }
            if (prodWorkflowId) {
                productionWorkflow = prodWorkflowId;
                if (prodSubJobName) {
                    productionWorkflow += `/${prodSubJobName}`;
                }
            }
            break;
        }
        case IntegrationType.TravisCI: {
            const { repoSlug } = buildSystemIntegration.data;

            if (repoSlug) {
                RCWorkflow = `${repoSlug}`;
            }

            break;
        }
        case IntegrationType.CustomCI: {
            RCWorkflow = `${buildSystemIntegration.installationId}`;
            break;
        }
        case IntegrationType.XcodeCloud: {
            const { workflowId, devWorkflowId, prodWorkflowId, productName, devProductName, prodProductName } =
                buildSystemIntegration.data;

            if (workflowId) {
                RCWorkflow = `${productName}/${workflowId}`;
            }
            if (devWorkflowId) {
                workingBranchWorkflow = `${devProductName}/${devWorkflowId}`;
            }
            if (prodWorkflowId) {
                productionWorkflow = `${prodProductName}/${prodWorkflowId}`;
            }
            break;
        }
    }

    if (shortString) {
        return {
            workingBranchWorkflow: last(workingBranchWorkflow?.split('/')),
            RCWorkflow: last(RCWorkflow?.split('/')),
            productionWorkflow: last(productionWorkflow?.split('/')),
        };
    }

    return {
        workingBranchWorkflow,
        RCWorkflow,
        productionWorkflow,
    };
};

export const getBucketColor = (bucketName: string, bucketType: BucketType): string => {
    switch (bucketType) {
        case BucketType.Development:
            return antGold.gold6;
        case BucketType.RC:
            return antGreen.green6;
        case BucketType.Personal:
            return antBlue.blue6;
        case BucketType.Custom: {
            if (!bucketName) {
                return BUILD_BUCKET_COLORS[0];
            }

            let sum = 0;

            for (const char of bucketName) {
                sum += char.charCodeAt(0);
            }
            const index = sum % BUILD_BUCKET_COLORS.length;
            return BUILD_BUCKET_COLORS[index];
        }
    }
};

export const disabledIntegrationMessageForContext: (context: SetupStepsCallerContext) => string | null = (context) => {
    switch (context) {
        case SetupStepsCallerContext.BuildDistroConnectCICDIntegration:
            return 'This integration is not supported for build distribution in Runway';
        default:
            return null;
    }
};

export const bucketDescriptionForType: (type: BucketType) => string | null = (type) => {
    switch (type) {
        case BucketType.Development:
            return "A bucket for your team's development or alpha builds.";
        case BucketType.Personal:
            return 'A free-form bucket for any and all kinds of work-in-progress builds. Use for yourself or to share builds with others.';
        case BucketType.RC:
            return 'A bucket for all release candidate builds.';
        default:
            return null;
    }
};

export const displayNameForBuildDistroBucketRuleType: (type: BuildDistroBucketRuleType) => string = (type) => {
    switch (type) {
        case BuildDistroBucketRuleType.Branch:
            return 'Branch rule';
        case BuildDistroBucketRuleType.PR:
            return 'Pull request rule';
    }
};

export const listEventsAndParams: (eventsAndParams?: EventAndParam[]) => string = (eventsAndParams) => {
    return eventsAndParams && eventsAndParams.length > 0 ?
            eventsAndParams
                ?.map((eventAndParam) => {
                    if (eventAndParam.paramName) {
                        return `${eventAndParam.eventName} - ${eventAndParam.paramName}`;
                    }
                    return eventAndParam.eventName;
                })
                .join(', ')
        :   'No events selected';
};

export const listDatadogEventsParamsAndCustomMetrics = (
    eventsAndParams?: EventAndParam[],
    customMetrics?: DatadogCustomMetric[],
): string => {
    const selectedItems = [
        ...(eventsAndParams?.length ? [listEventsAndParams(eventsAndParams)] : []),
        ...(customMetrics ?? []).map(datadogCustomMetricDisplayName),
    ];
    return selectedItems.length ? selectedItems.join(', ') : 'No events or metrics selected';
};

export const eventPropertyTerminology: (
    integrationType: IntegrationType,
    shouldCapitalize?: boolean,
) => [string, string] = (integrationType, shouldCapitalize) => {
    switch (integrationType) {
        case IntegrationType.MixpanelAnalytics:
        case IntegrationType.Amplitude:
            return shouldCapitalize ? ['Property', 'Properties'] : ['property', 'properties'];
        case IntegrationType.NewRelicAnalytics:
            return shouldCapitalize ? ['Attribute', 'Attributes'] : ['attribute', 'attributes'];
        case IntegrationType.GoogleAnalytics:
        case IntegrationType.DatadogAnalytics:
        default:
            return shouldCapitalize ? ['Metric', 'Metrics'] : ['metric', 'metrics'];
    }
};

export const displayDeviceClass: (deviceClass: DeviceClass) => string = (deviceClass) => {
    switch (deviceClass) {
        case DeviceClass.iPhone:
            return 'iPhone';
        case DeviceClass.iPad:
            return 'iPad';
        case DeviceClass.iPod:
            return 'iPod';
        case DeviceClass.AppleTV:
            return 'Apple TV';
        case DeviceClass.Mac:
            return 'Mac';
        case DeviceClass.AppleWatch:
            return 'Apple Watch';
        case DeviceClass.AppleVisionPro:
            return 'Apple Vision Pro';
    }
};

export const displayTextForChecklistPlacement = (placement: ChecklistItemPlacement) => {
    switch (placement) {
        case ChecklistItemPlacement.StepChecklist:
            return 'Checklist';
        case ChecklistItemPlacement.Approvals:
            return 'Approval';
        case ChecklistItemPlacement.RegressionTesting:
            return 'Regression Testing';
    }
};

export const displayASCRegistrationStatusDescription: (status: ASCRegistrationStatus) => string = (status) => {
    switch (status) {
        case ASCRegistrationStatus.Enabled:
            return 'This device is registered in the Apple Developer Portal.';
        case ASCRegistrationStatus.Disabled:
            return 'This device is registered in the Apple Developer Portal, but is disabled.';
        case ASCRegistrationStatus.Ineligible:
            return 'This device is registered in the Apple Developer Portal, but is ineligible for testing.';
        default:
            return 'This device is not yet registered in the Apple Developer Portal.';
    }
};

export const placeholderForFileTypePlatform = (platform: AppPlatform, appStoreType: AppStoreType) => {
    switch (platform) {
        case AppPlatform.TvOS:
        case AppPlatform.Ios:
        case AppPlatform.VisionOS:
            return 'app.ipa';
        case AppPlatform.WearOS:
        case AppPlatform.Android:
            return appStoreType == AppStoreType.Amazon ? 'app.apk' : 'app.aab';
        default:
            return '';
    }
};

export const displayTextForAutoCherryPickStrategy: (strategy: AutoCherryPickStrategy) => string = (strategy) => {
    switch (strategy) {
        case AutoCherryPickStrategy.SingleInSequence:
            return 'one at a time in the order in which they were merged';
        case AutoCherryPickStrategy.SingleInParallel:
            return `in the order in which they were merged, as soon as they're detected`;
        case AutoCherryPickStrategy.Cumulative:
            return `by grouping all fixes into a single PR, in the order in which they were merged, as soon as they're detected`;
    }
};

export const helpTextForAutoCherryPickStrategy: (strategy: AutoCherryPickStrategy) => string = (strategy) => {
    switch (strategy) {
        case AutoCherryPickStrategy.SingleInSequence:
            return 'Pull requests containing fixes against the release branch must be merged or closed before the next fix can be pulled in';
        case AutoCherryPickStrategy.SingleInParallel:
            return 'Multiple pull requests containing fixes against the release branch can be open at the same time';
        case AutoCherryPickStrategy.Cumulative:
            return 'A single pull request containing all fixes will be open against the release branch. You must manually merge this PR once all fixes are in';
    }
};

export const selectedProjectNameForTranslationsIntegration: (integration: Integration) => string | null | undefined = (
    integration,
) => {
    switch (integration?.id) {
        case IntegrationType.Crowdin:
            return integration.data?.selectedProject?.name;
        case IntegrationType.Lokalise:
            return integration.data?.selectedProject?.name;
        default:
            return null;
    }
};

export const selectedMetadataProjectNameForTranslationsIntegration: (
    integration: Integration,
) => string | null | undefined = (integration) => {
    switch (integration?.id) {
        case IntegrationType.Crowdin:
            return integration.data?.selectedMetadataProject?.name ?? integration.data?.selectedProject?.name;
        case IntegrationType.Lokalise:
            return integration.data?.selectedMetadataProject?.name ?? integration.data?.selectedProject?.name;
        default:
            return null;
    }
};

export const selectedProjectURLForTranslationsIntegration: (integration: Integration) => string | null | undefined = (
    integration,
) => {
    switch (integration?.id) {
        case IntegrationType.Crowdin:
            return integration.data?.selectedProject?.url;
        case IntegrationType.Lokalise:
            return integration.data?.selectedProject?.url;
        default:
            return null;
    }
};

export const selectedMetadataProjectURLForTranslationsIntegration: (
    integration: Integration,
) => string | null | undefined = (integration) => {
    let url: string | null | undefined = null;
    switch (integration?.id) {
        case IntegrationType.Crowdin:
            url = integration.data?.selectedMetadataProject?.url;
            break;
        case IntegrationType.Lokalise:
            url = integration.data?.selectedMetadataProject?.url;
            break;
        default:
            return null;
    }
    return url ?? selectedProjectURLForTranslationsIntegration(integration);
};

export const exportTranslationsExplainerMessage = () => {
    return `An export will be triggered for any localizable string files with at least one key in an "Export
    needed" state. Any incomplete string translations that may also be included in the file will
    also be exported. Triggering an export will open a PR against the target branch with all exported translation files.`;
};

export const uploadTranslationsExplainerMessage = () => {
    return `Any localizable string files containing one or more keys in an "Upload needed" state will be uploaded to the translations provider. `;
};

export const displayMetadataFieldTranslationsStatus = (status: MetadataTranslationStatus) => {
    switch (status) {
        case MetadataTranslationStatus.Synced:
            return 'Synced';
        case MetadataTranslationStatus.TranslationsPending:
            return 'Waiting for translations';
        case MetadataTranslationStatus.UploadNeeded:
            return 'Upload needed';
        case MetadataTranslationStatus.ExportNeeded:
            return 'Export needed';
        case MetadataTranslationStatus.NotFound:
            return 'Locale not found';
    }
};

export const getMetadataFieldTranslationsStatusTooltipMessage = (
    status: MetadataTranslationStatus,
    appStoreName: string,
    translationsIntegrationProviderName: string,
    appStoreVersionExists: boolean,
) => {
    switch (status) {
        case MetadataTranslationStatus.Synced:
            if (appStoreVersionExists) {
                return `translations complete and synced to ${appStoreName}.`;
            } else {
                return 'translations complete and saved to Runway.';
            }
        case MetadataTranslationStatus.TranslationsPending:
            return `waiting for translations from ${translationsIntegrationProviderName}.`;
        case MetadataTranslationStatus.UploadNeeded:
            return `this field's value was updated in Runway and needs to be uploaded for re-translation. Use the 'Sync' button to upload and request new translations from ${translationsIntegrationProviderName}.`;
        case MetadataTranslationStatus.ExportNeeded:
            return `translations are complete in ${translationsIntegrationProviderName}, but still need to be exported and saved to ${appStoreName}. Use the 'Sync' button to export and save all updated translation keys.`;
        case MetadataTranslationStatus.NotFound:
            return `translations for this locale were not found in ${translationsIntegrationProviderName}.`;
    }
};

export const getIconForMetadataFieldTranslationsStatus = (status: MetadataTranslationStatus) => {
    switch (status) {
        case MetadataTranslationStatus.Synced:
            return CheckCircleFilled;
        case MetadataTranslationStatus.TranslationsPending:
            return ClockCircleFilled;
        case MetadataTranslationStatus.UploadNeeded:
            return CloudUploadOutlined;
        case MetadataTranslationStatus.ExportNeeded:
            return CloudDownloadOutlined;
        case MetadataTranslationStatus.NotFound:
            return MinusCircleOutlined;
    }
};

export const getColorForMetadataFieldTranslationsStatus: (status: MetadataTranslationStatus) => string = (status) => {
    switch (status) {
        case MetadataTranslationStatus.Synced:
            return runwayColors.statusGreen;
        case MetadataTranslationStatus.TranslationsPending:
        case MetadataTranslationStatus.UploadNeeded:
        case MetadataTranslationStatus.ExportNeeded:
            return runwayColors.statusYellow;
        case MetadataTranslationStatus.NotFound:
            return antGrey.grey7;
    }
};

export const getUserShortNamesFromUserIds: (orgUsers: OrgUser[], userIds: string[]) => string[] = (
    orgUsers,
    userIds,
) => {
    const userShortNames: string[] = [];

    if (userIds === null || userIds.length === 0) {
        return userShortNames;
    }

    if (orgUsers === null || orgUsers.length === 0) {
        return userIds;
    }

    for (const userId of userIds) {
        const orgUser = orgUsers.find((orgUser) => orgUser.id === userId);
        if (orgUser != null) {
            userShortNames.push(displayUserNameShort(orgUser));
        }
    }

    return userShortNames;
};

export const getRolloutChecklistItemsOwners: (
    checklistItem: ChecklistItem,
    orgUsers: OrgUser[],
    orgGroups: Record<UserGroupId, UserGroup>,
) => string[] = (checklistItem, orgUsers, orgGroups) => {
    let owners: string[] = [];

    if (checklistItem?.owners != null && checklistItem?.owners.length > 0) {
        owners = getUserShortNamesFromUserIds(orgUsers, checklistItem.owners);
    } else if (checklistItem?.approverGroups != null && checklistItem?.approverGroups.length > 0) {
        owners = checklistItem.approverGroups.map((approverGroupId) => {
            const group = orgGroups[approverGroupId];
            return displayTextForUserChecklistGroup(group);
        });
    }

    return owners;
};

export const getUserActionDisplayName = (userAction: RunwayUserAction) => {
    switch (userAction) {
        case RunwayUserAction.UAAddRemoveEditIntegration:
            return 'Add, update, delete integrations';
        case RunwayUserAction.UATriggerIntegrationRefresh:
            return 'Trigger integration refresh';
        case RunwayUserAction.UAUpdateAppSettings:
            return 'Update app settings';
        case RunwayUserAction.UAEditAutomations:
            return 'Update automations';
        case RunwayUserAction.UACreateDeleteApp:
            return 'Create, delete apps';
        case RunwayUserAction.UACreateSkipDeleteCompleteReleases:
            return 'Create, update, delete release';
        case RunwayUserAction.UACreateHotfix:
            return 'Create hotfix';
        case RunwayUserAction.UAUpdateScheduleCadence:
            return 'Update schedule cadence';
        case RunwayUserAction.UAUpdateReleaseTargetDates:
            return 'Update release target dates';
        case RunwayUserAction.UACreateBranchPromoteCode:
            return 'Create release branches / promote code';
        case RunwayUserAction.UABumpVersion:
            return 'Bump version';
        case RunwayUserAction.UATagCommit:
            return 'Tag a release';
        case RunwayUserAction.UAApproveScreenshotsMetadata:
            return 'Approve screenshots/metadata';
        case RunwayUserAction.UAPromoteBetaBuildToTestingTrack:
            return 'Promote/assign beta build to testing track';
        case RunwayUserAction.UAApproveBetaTesting:
            return 'Approve Beta testing step (end soak)';
        case RunwayUserAction.UAUpdateRegressionStatus:
            return 'Update regression status';
        case RunwayUserAction.UASetResumeActiveRCBuild:
            return 'Resume auto-selection of latest RC build';
        case RunwayUserAction.UATriggerCIWorkflow:
            return 'Run CI/CD workflows';
        case RunwayUserAction.UAUpdateAppStoreSelectedBuild:
            return 'Change selected build in App submission';
        case RunwayUserAction.UASubmitAppUpdate:
            return 'Submit app update';
        case RunwayUserAction.UADevRejectAppUpdate:
            return 'Pull app update from review';
        case RunwayUserAction.UAReleaseAppUpdate:
            return 'Release app update';
        case RunwayUserAction.UAUpdateReleasePilot:
            return 'Update release pilot';
        case RunwayUserAction.UAAddRemoveEditCLI:
            return 'Add/remove/edit checklist items';
        case RunwayUserAction.UAPingCLI:
            return 'Ping checklist items';
        case RunwayUserAction.UAToggleApprovalCLI:
            return 'Complete/uncomplete checklist items';
        case RunwayUserAction.UAToggleFRIgnore:
            return 'Ignore Feature Readiness work items';
        case RunwayUserAction.UAHaltRollout:
            return 'Halt rollout';
        case RunwayUserAction.UAResumeRollout:
            return 'Resume rollout';
        case RunwayUserAction.UAUpdateRolloutPercentage:
            return 'Edit rollout percentage';
        case RunwayUserAction.UAAddRemoveEditReleaseBranchPatterns:
            return 'Add, update, or delete release branch patterns';
        case RunwayUserAction.UAAddRemoveEditReleaseTagPatterns:
            return 'Add, update, or delete release tag patterns';
        case RunwayUserAction.UAAddRemoveEditFeatureAffiliations:
            return 'Add, update, or delete feature affiliation patterns';
        case RunwayUserAction.UAAssignBetaTestersToBuilds:
            return 'Assign beta tester to builds';
        case RunwayUserAction.UAAssignBetaGroupsToBuilds:
            return 'Assign beta groups to builds';
        case RunwayUserAction.UAUpdateAdditionalBranchConfig:
            return 'Update additional branches configuration';
        case RunwayUserAction.UADownloadBuildArtifact:
            return 'Download build artifacts';
        case RunwayUserAction.UARetryArtifactUpload:
            return 'Retry artifact upload';
        case RunwayUserAction.UAUpdateMetadata:
            return 'Update release metadata';
        case RunwayUserAction.UAEditReleasePlanningSummary:
            return 'Edit release planning summary';
        case RunwayUserAction.UAEditReleaseSummaryMessage:
            return 'Edit release summary';
        case RunwayUserAction.UAEditAppStoreReleaseSettings:
            return 'Edit app store release settings';
        case RunwayUserAction.UAApplyMetadata:
            return 'Apply metadata from other source';
        case RunwayUserAction.UASubmitBetaBuildForReview:
            return 'Submit build for beta review';
        case RunwayUserAction.UAPauseResumeScheduleCadence:
            return 'Pause/resume schedule cadence';
        case RunwayUserAction.UAUpdateBetaTestingNotes:
            return 'Add/update beta testing notes';
        case RunwayUserAction.UAAccessSSOPortal:
            return 'Access SSO/SAML settings';
        case RunwayUserAction.UAAccessDirectorySyncPortal:
            return 'Access the Directory Sync portal';
        case RunwayUserAction.UAInviteUsers:
            return 'Invite users';
        case RunwayUserAction.UAEditUserGroups:
            return 'Edit user groups';
        case RunwayUserAction.UARemoveOrgUser:
            return 'Remove users';
        case RunwayUserAction.UABuildDistroOptIn:
            return 'Build Distro opt in';
        case RunwayUserAction.UACreateBuildDistroBucket:
            return 'Create Build Distro bucket';
        case RunwayUserAction.UABucketUpdateSettings:
            return 'Update bucket settings';
        case RunwayUserAction.UABucketUpdateNotifications:
            return 'Update bucket notifications';
        case RunwayUserAction.UABucketUpdateBucketMembers:
            return 'Update bucket members';
        case RunwayUserAction.UABucketInviteIndividualTestersToBuilds:
            return 'Invite testers to individual builds';
        case RunwayUserAction.UABucketUploadBuilds:
            return 'Upload builds / trigger builds';
        case RunwayUserAction.UABucketUpdateBuildTestersNotes:
            return 'Update bucket build tester notes';
        case RunwayUserAction.UABucketInstallBuilds:
            return 'Install bucket builds';
        case RunwayUserAction.UAUpdateDirectorySyncUserGroupsForGroups:
            return 'Update Directory Sync user groups for groups mapping';
        case RunwayUserAction.UAStartRollbackResigningSequence:
            return 'Start rollback re-signing sequence';
        case RunwayUserAction.UAAddRemoveDevice:
            return 'Add/remove user device';
        case RunwayUserAction.UAAddRemoveDeviceFromApp:
            return 'Add/remove test device in Runway app';
        case RunwayUserAction.UAUpdateDeviceInASC:
            return 'Register/enable/disable devices with Apple';
        case RunwayUserAction.UAUploadSigningKey:
            return 'Upload signing keys to Runway';
        case RunwayUserAction.UAImportExportTranslationStrings:
            return 'Upload or export translations strings to/from provider';
        case RunwayUserAction.UAApproveTranslations:
            return 'Approve Translations step';
        case RunwayUserAction.UAAddFixRequest:
            return 'Add fix / fix request';
        case RunwayUserAction.UAUpdateFixRequestStatus:
            return 'Update fix request status';
        case RunwayUserAction.UAUpdateAppStoreReviewSubmission:
            return 'Add/remove submission items';
        case RunwayUserAction.UAUploadExportMetadataTranslations:
            return 'Upload or export metadata translations';
        case RunwayUserAction.UACreateEditDeleteCustomGroup:
            return 'Create, update, delete user groups';
        case RunwayUserAction.UACreateDeleteAPITokens:
            return 'Create or delete API tokens';
        case RunwayUserAction.UAAddUpdateRemoveWebhook:
            return 'Add/update/remove webhooks';
        case RunwayUserAction.UAAddRemoveTestDeviceFromApp:
            return 'Add/remove test devices';
        case RunwayUserAction.UAUpdateApprovalItemsStatus:
            return 'Update approval items status';
        case RunwayUserAction.UAAddRemoveEditApprovalItems:
            return 'Add/remove/edit approval items';
        case RunwayUserAction.UAIngestCustomOBAAMetrics:
            return 'Ingest custom observability metrics';
        case RunwayUserAction.UAUpdateRegressionItemsStatus:
            return 'Update regression items status';
        case RunwayUserAction.UAAddRemoveUsersFromPilotRotation:
            return 'Update pilot rotation';
        case RunwayUserAction.UAAddRemoveEditRegressionItems:
            return 'Add/remove/edit regression items';
        case RunwayUserAction.UAUpdateOrgSettings:
            return 'Update org settings';
        case RunwayUserAction.UABucketArchiveBuilds:
            return 'Archive Build Distro builds';
        case RunwayUserAction.UAStartRollout:
            return 'Start rollout';
        case RunwayUserAction.UAUpdatePhasedRelease:
            return 'Update phased release';
        case RunwayUserAction.UACreateEditDeleteOrgTags:
            return 'Create, update, delete org tags';
        case RunwayUserAction.UAManageTriageIssues:
            return 'Manage Triage issues';
        default:
            return userAction;
    }
};

export const tooltipDescriptionsForEmailNotifs = {
    EmailNotificationOrgMetricsDigest:
        'A weekly digest detailing key release process metrics across all apps in your organization in Runway',
    EmailNotificationOrgReleasesDigest:
        'A biweekly summary of active and upcoming releases for all apps in the organization',
    EmailNotificationAppReleasesDigest: 'A weekly summary of live, active and upcoming releases for a given app',
    EmailNotificationReleaseSummary:
        'A summary sent as soon as releases are completed highlighting important details of the release',
};

export const displayLifecycleEventName = (lifecycleEventType: LifecycleEventType) => {
    switch (lifecycleEventType) {
        case LifecycleEventType.LifecycleEventTypeKickoff:
            return 'Kickoff';
        case LifecycleEventType.LifecycleEventTypeSubmit:
            return 'Submit';
        case LifecycleEventType.LifecycleEventTypeRelease:
            return 'Release';
        case LifecycleEventType.LifecycleEventTypeRolloutStart:
            return 'Rollout start';
        case LifecycleEventType.LifecycleEventTypeRolloutCompletion:
            return 'Rollout completion';
        case LifecycleEventType.LifecycleEventTypeRolloutIncrement:
            return 'Rollout - Day';
        case LifecycleEventType.LifecycleEventTypeBlackoutPeriod:
            return 'Blackout period';
        case LifecycleEventType.LifecycleEventTypeLifecycleFreeze:
            return 'Lifecycle freeze';
        default:
            return '';
    }
};

export const tooltipDisplayLifecycleEventName = (lifecycleEventType: LifecycleEventType) => {
    switch (lifecycleEventType) {
        case LifecycleEventType.LifecycleEventTypeKickoff:
            return 'Kickoff';
        case LifecycleEventType.LifecycleEventTypeSubmit:
            return 'Submit';
        case LifecycleEventType.LifecycleEventTypeRelease:
            return 'Release';
        case LifecycleEventType.LifecycleEventTypeRolloutStart:
            return 'Rollout start';
        case LifecycleEventType.LifecycleEventTypeRolloutCompletion:
            return 'Rollout completion';
        case LifecycleEventType.LifecycleEventTypeRolloutIncrement:
            return 'Rollout';
        case LifecycleEventType.LifecycleEventTypeBlackoutPeriod:
            return 'Blackout period (rollout)';
        case LifecycleEventType.LifecycleEventTypeReleaseCycle:
            return 'Release cycle';
        case LifecycleEventType.LifecycleEventTypeExternalEvent:
            return 'External event';
        case LifecycleEventType.LifecycleEventTypeLifecycleFreeze:
            return 'Lifecycle freeze';
        case LifecycleEventType.LifecycleEventTypeArbitrary:
            return 'Other...';
    }
};

export const relativeTimingLabel = (timing: RelativeTiming) => {
    switch (timing) {
        case RelativeTiming.before:
            return 'before';
        case RelativeTiming.after:
            return 'after';
        case RelativeTiming.onTime:
            return 'at the time of';
    }
};

export const displayLifecycleEventTime = (dateString: string) => {
    const date = dayjs(dateString);
    if (date.minute() === 0) {
        return date.format('hA').toLowerCase();
    }
    return date.format('h:mmA').toLowerCase();
};

export const displayLifecycleEventFullTime = (dateString: string) => {
    const date = dayjs(dateString);
    return date.format('h:mmA').toLowerCase();
};

export const formatDateString = (dateString: string, dateFormat: string) => {
    const date = dayjs(dateString);
    return date.format(dateFormat);
};

export const formatStartAndEndHourString = (
    startDateString: string,
    shouldDisplayEndDateForType: boolean,
    endDateString?: string,
    timezone?: string,
    allDay?: boolean,
    hideTimezone?: boolean,
) => {
    const parseDate = (dateString: string) => {
        if (allDay) {
            if (timezone?.length) {
                return dayjs(dateString).tz(timezone).startOf('day');
            }
            return dayjs(dateString).startOf('day');
        }
        return dayjs(dateString);
    };

    const startDate = parseDate(startDateString);

    const defaultDateFormat = hideTimezone ? 'h:mm A' : 'h:mm A z';

    if (!shouldDisplayEndDateForType || !endDateString) {
        return [startDate.format(defaultDateFormat), null];
    }

    const endDate = parseDate(endDateString);

    if (startDate.isSame(endDate, 'day')) {
        return [`${startDate.format('h:mm A')} - ${endDate.format(defaultDateFormat)}`, null];
    }

    return [startDate.format(defaultDateFormat), endDate.format(defaultDateFormat)];
};

export const formatEndDateStringForReleaseCalendar = (dateString: string, dateFormat: string) => {
    const date = dayjs(dateString);
    const updatedDate = dayjs(date).subtract(1, 'day');
    return updatedDate.format(dateFormat);
};

export const getCalendarEventPopoverIcon = (
    eventType: LifecycleEventType,
    eventDateType: ReleaseCalendarEventType,
    date: string,
    noEarlierThanTargetEnabled: boolean,
    isTargetDateScheduledFromCadence: boolean,
    isAcceleratedRollout?: boolean,
) => {
    if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarExternalEvent) {
        return '';
    }
    if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate) {
        if (isAcceleratedRollout) {
            return ThunderboltFilled;
        }
        return ClockCircleOutlined;
    } else if (
        isTargetDateScheduledFromCadence &&
        eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate
    ) {
        return RetweetOutlined;
    } else if (
        noEarlierThanTargetEnabled &&
        eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
        (eventType === LifecycleEventType.LifecycleEventTypeSubmit ||
            eventType === LifecycleEventType.LifecycleEventTypeRelease)
    ) {
        return LogoutOutlined;
    }
    return null;
};

const getCalendarExternalEventDescription = (integration: IntegrationType) => {
    switch (integration) {
        case IntegrationType.GoogleCalendar:
        case IntegrationType.GooglePersonalCalendar:
            return 'Synced from Google Calendar';
        case IntegrationType.OutlookCalendar:
            return 'Synced from Outlook Calendar';
        default:
            return '';
    }
};

export const getCalendarEventDescription = (
    eventType: LifecycleEventType,
    eventDateType: ReleaseCalendarEventType,
    noEarlierThanTargetEnabled: boolean,
    scheduleCadenceSettings: ReleaseScheduleCadenceSettings,
    isTargetDateScheduledFromCadence: boolean,
    isReleaseSkipped: boolean,
    integrationType?: IntegrationType,
    isAcceleratedRollout?: boolean,
) => {
    if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarExternalEvent) {
        return getCalendarExternalEventDescription(integrationType!);
    }
    if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent) {
        return 'Selected lifecycle automations are paused';
    }
    if (
        isReleaseSkipped &&
        (eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
            eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
    ) {
        return 'Release was skipped';
    } else if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate) {
        if (isAcceleratedRollout) {
            return 'Accelerated rollout to 100% if stable';
        }
        return 'Projected date and time';
    } else if (
        isTargetDateScheduledFromCadence &&
        eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate
    ) {
        let cadenceText = '';
        if (scheduleCadenceSettings.scheduleCadence === ReleaseScheduleCadenceStyle.Weekly) {
            cadenceText = 'Every week';
        } else if (scheduleCadenceSettings.scheduleCadence === ReleaseScheduleCadenceStyle.Biweekly) {
            cadenceText = 'Every two weeks';
        } else if (scheduleCadenceSettings.scheduleCadence === ReleaseScheduleCadenceStyle.ThreeWeeks) {
            cadenceText = 'Every three weeks';
        } else if (scheduleCadenceSettings.scheduleCadence === ReleaseScheduleCadenceStyle.Monthly) {
            cadenceText = 'Every four weeks';
        } else if (scheduleCadenceSettings.scheduleCadence == ReleaseScheduleCadenceStyle.SixWeeks) {
            cadenceText = 'Every six weeks';
        }

        const cadenceDayOfWeek = getScheduleCadenceDayOfWeekStringForEvent(eventType, scheduleCadenceSettings);
        if (cadenceDayOfWeek != null) {
            const dayOfWeekText = ' on ' + cadenceDayOfWeek;
            cadenceText += dayOfWeekText;
        }
        return cadenceText + '. Executes within 5 minutes after the target date.';
    } else if (
        noEarlierThanTargetEnabled &&
        eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
        (eventType === LifecycleEventType.LifecycleEventTypeSubmit ||
            eventType === LifecycleEventType.LifecycleEventTypeRelease)
    ) {
        return 'No earlier than above date/time. Executes within 5 minutes after the target date.';
    } else if (
        eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
        (eventType === LifecycleEventType.LifecycleEventTypeKickoff ||
            eventType === LifecycleEventType.LifecycleEventTypeSubmit ||
            eventType === LifecycleEventType.LifecycleEventTypeRelease)
    ) {
        return 'Executes within 5 minutes after the target date.';
    }
    return null;
};

export const getScheduleCadenceDayOfWeekStringForEvent = (
    eventType: LifecycleEventType,
    scheduleCadenceSettings: ReleaseScheduleCadenceSettings,
) => {
    if (isMultipleReleasesCadence(scheduleCadenceSettings)) {
        return makeDaysOfWeekStringForMultipleReleasesCadence(eventType, scheduleCadenceSettings.cadenceTargetDateSets);
    } else if (scheduleCadenceSettings?.cadenceTargetDateSets?.length === 1) {
        const dateSet = scheduleCadenceSettings.cadenceTargetDateSets[0];

        if (eventType === LifecycleEventType.LifecycleEventTypeKickoff && dateSet.kickoffDayOfWeek != null) {
            return displayTextForDayOfTheWeek(dateSet.kickoffDayOfWeek);
        } else if (eventType === LifecycleEventType.LifecycleEventTypeSubmit && dateSet.submitDayOfWeek != null) {
            return displayTextForDayOfTheWeek(dateSet.submitDayOfWeek);
        } else if (eventType === LifecycleEventType.LifecycleEventTypeRelease && dateSet.releaseDayOfWeek != null) {
            return displayTextForDayOfTheWeek(dateSet.releaseDayOfWeek);
        }
    }

    return null;
};

export const makeDaysOfWeekStringForMultipleReleasesCadence = (
    eventType: LifecycleEventType,
    cadenceTargetDateSets?: CadenceTargetDateSet[],
) => {
    if (!cadenceTargetDateSets) {
        return null;
    }

    let result: string = '';

    for (let i = 0; i < cadenceTargetDateSets.length; i++) {
        const dateSet = cadenceTargetDateSets[i];

        let day: string | undefined;
        if (eventType === LifecycleEventType.LifecycleEventTypeKickoff && dateSet.kickoffDayOfWeek != null) {
            day = displayTextForDayOfTheWeek(dateSet.kickoffDayOfWeek);
        } else if (eventType === LifecycleEventType.LifecycleEventTypeSubmit && dateSet.submitDayOfWeek != null) {
            day = displayTextForDayOfTheWeek(dateSet.submitDayOfWeek);
        } else if (eventType === LifecycleEventType.LifecycleEventTypeRelease && dateSet.releaseDayOfWeek != null) {
            day = displayTextForDayOfTheWeek(dateSet.releaseDayOfWeek);
        }

        let dayString: string;
        if (day) {
            if (i === 0) {
                dayString = day;
            } else if (i === cadenceTargetDateSets.length - 1) {
                dayString = ' and ' + day;
            } else {
                dayString = ', ' + day;
            }
        } else {
            // For multiple releases cadences, a given target type should be configured in all sets, if configured in any.
            return null;
        }
        result = result + dayString;
    }

    return result;
};

export const displayCompletedLifecycleEventName: (type: LifecycleEventType) => string = (type) => {
    switch (type) {
        case LifecycleEventType.LifecycleEventTypeKickoff:
            return 'Kicked off';
        case LifecycleEventType.LifecycleEventTypeSubmit:
            return 'Submitted';
        case LifecycleEventType.LifecycleEventTypeRelease:
            return 'Released';
        case LifecycleEventType.LifecycleEventTypeRolloutCompletion:
            return 'Rollout complete';
        case LifecycleEventType.LifecycleEventTypeRolloutStart:
            return 'Rollout started';
        case LifecycleEventType.LifecycleEventTypeRolloutIncrement:
            return 'Rollout updated';
        case LifecycleEventType.LifecycleEventTypeBlackoutPeriod:
            return 'Blackout period';
        case LifecycleEventType.LifecycleEventTypeLifecycleFreeze:
            return 'Lifecycle freeze';
        default:
            return '';
    }
};

export const displayLifecycleEventFormattedTitle = (
    eventType: LifecycleEventType,
    eventDateType: ReleaseCalendarEventType,
    isEventComplete: boolean,
) => {
    let eventName = tooltipDisplayLifecycleEventName(eventType);
    let formattedEventName = '';

    if (isEventComplete && eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate) {
        return displayCompletedLifecycleEventName(eventType);
    }

    if (eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate) {
        formattedEventName = `Target ${eventName}`;
    } else {
        formattedEventName = eventName;
    }

    return capitalizeFirstLetter(formattedEventName);
};

export const codeStyle = (fontSize: string) => {
    return {
        color: antGrey.grey7,
        fontFamily: 'monospace',
        fontSize: fontSize,
    };
};

export const displayTimelineEventType = (eventType: TimelineEventType) => {
    if (eventType === TimelineEventType.AutoBumpVersionUpcoming) {
        return 'Bump version';
    } else if (eventType === TimelineEventType.AutoCreateRunwayReleasesUpcoming) {
        return 'Create upcoming version in Runway';
    } else if (eventType === TimelineEventType.AutoTagReleasesUpcoming) {
        return 'Tag releases';
    } else {
        return '';
    }
};

export const automationDisplayNameForAutomationId = (automationType: AutomationType, ast: AppStoreType) => {
    switch (automationType) {
        case AutomationType.AutoSelectLatestBuild:
            switch (ast) {
                case AppStoreType.Apple:
                    return 'Select build for App Store Connect';
                case AppStoreType.Google:
                    return 'Add APK/AAB to the production track';
                case AppStoreType.Amazon:
                    return 'Select apk in the current edit';
                case AppStoreType.Samsung:
                    // not supported for Samsung
                    return '';
                case AppStoreType.Huawei:
                    // not supported for Huawei
                    return '';
                default:
                    return '';
            }
        case AutomationType.AutoBackmergeReleaseBranches:
            return 'Backmerge changes from the release branch';
        case AutomationType.AutoApplyBetaBuildNotes:
            return 'Apply beta testing notes to new beta builds';
        case AutomationType.AutoGenerateCIBuildDiffSummary:
            return 'Auto-generate summary of diff in CI builds';
        case AutomationType.AutoCreateAppStoreVersions:
            switch (ast) {
                case AppStoreType.Apple:
                    return 'Create and sync App Store Connect versions as needed to match current Runway release';
                case AppStoreType.Google:
                    return 'Create and sync draft production release in the Play Console to match current Runway release';
                case AppStoreType.Amazon:
                    return 'Create and sync an edit in the Amazon Developer Console to match current Runway release';
                case AppStoreType.Samsung:
                    return '';
                case AppStoreType.Huawei:
                    return '';
                default:
                    return '<MISSING>';
            }
        case AutomationType.AutoCreateRunwayReleases:
            return 'Create upcoming version in Runway when current release is kicked off';
        case AutomationType.AutoTagReleases:
            return 'Tag releases at the end of the release cycle';
        case AutomationType.AutoMergeRunwayPRs:
            return 'Merge pull requests opened by Runway';
        case AutomationType.AutoDownloadArtifacts:
            return 'Enable artifact downloads';
        case AutomationType.AutoUploadBinaryArtifactToAppStore:
            return 'Upload build artifacts for distribution';
        case AutomationType.AutoUploadDsymsToStabilityMonitoring:
            return 'Upload dSYMs to stability monitoring';
        case AutomationType.AutoUploadReleaseAssets:
            return 'Attach build artifacts to VCS release record when tagging';
        case AutomationType.AutoApplyMetadata:
            return 'Populate store metadata to new releases';
        case AutomationType.AutoKickoffRelease:
            return 'Kick off release on target date';
        case AutomationType.AutoSubmitRelease:
            return 'Submit app for review';
        case AutomationType.AutoPullSubmissionFromReview:
            return 'Pull / developer reject a submission from review';
        case AutomationType.AutoRelease:
            return 'Release app';
        case AutomationType.AutoBumpVersion:
            return 'Bump version';
        case AutomationType.AutoKickoffTriggerWorkflow:
            return 'Trigger workflow after release kickoff';
        case AutomationType.AutoTriggerCIWorkflow:
            return 'Trigger CI workflow';
        case AutomationType.AutoCreateIncidents:
            return 'Create incident/alert in incident management & scheduling tool';
        case AutomationType.AutoMarkReleaseLiveInStabilityMonitoringAndOBAA:
            return 'Mark release live in stability monitoring and observability & analytics tools';
        case AutomationType.AutoSubmitBetaBuilds:
            return 'Submit new builds for beta review';
        case AutomationType.AutoAssignBetaBuilds:
            return 'Assign beta testing builds to default testing groups';
        case AutomationType.AutoPromoteBuildsToTestTrack:
            return 'Promote new builds to testing track';
        case AutomationType.AutoPromoteStablePhasedReleases:
            return 'Release stable phased releases to 100% of users';
        case AutomationType.AutoHaltUnstablePhasedReleases:
            return 'Halt unstable phased releases';
        case AutomationType.AutoCreateReleaseChannels:
            return 'Create release-specific Slack or Microsoft Teams channel(s)';
        case AutomationType.AutoCompleteIssueTrackingReleases:
            return 'Mark releases as completed in project management tool';
        case AutomationType.AutoApplyReviewAttachments:
            return 'Apply default review attachment to new app versions in App Store Connect';
        case AutomationType.AutoSyncAppStoreUsers:
            switch (ast) {
                case AppStoreType.Apple:
                    return 'Sync users and groups from Runway to App Store Connect';
                case AppStoreType.Google:
                    return 'Sync users and groups from Runway to Google Play Console';
                case AppStoreType.Amazon:
                    return '';
                case AppStoreType.Samsung:
                    return '';
                case AppStoreType.Huawei:
                    return '';
                default:
                    return '<MISSING>';
            }
        case AutomationType.AutoDeleteReleaseBranch:
            return 'Delete version-specific release branches at the end of the release cycle';
        case AutomationType.AutoCherryPickFromWorkingBranch:
            return 'Pull (cherry-pick) fixes into the release';
        case AutomationType.AutoTransitionIssueTrackingTickets:
            return 'Update status of tickets upon milestone';
        case AutomationType.AutoStagedRollout:
            switch (ast) {
                case AppStoreType.Apple:
                case AppStoreType.Google:
                case AppStoreType.Huawei:
                    return 'Custom phased / staged rollout';
                case AppStoreType.Amazon:
                case AppStoreType.Samsung:
                    return '';
                default:
                    return '<MISSING>';
            }
        case AutomationType.AutoDistributeReleaseSummary:
            return 'Distribute release summary to additional recipients at the end of each release cycle';
        case AutomationType.AutoResignRollbackBuild:
            return 'Re-sign build for rollbacks';
        case AutomationType.AutoPrepareRollbackRelease:
            return 'Prepare rollback releases';
        case AutomationType.AutoCherryPickLocalizableStrings:
            return 'Update translations on the release branch from the working branch';
        case AutomationType.AutoSyncTranslations:
            return 'Sync localizable string translations';
        case AutomationType.AutoSyncSlackGroups:
            return 'Sync Slack groups';
        case AutomationType.AutoSyncSlackPilotUserGroup:
            return 'Sync Slack release pilot handle';
        case AutomationType.AutoSyncTeamsTags:
            return 'Sync Teams tags';
        case AutomationType.AutoSyncTeamsPilotTag:
            return 'Sync Teams release pilot handle';
        case AutomationType.AutoSyncAppleDevices:
            return 'Sync devices in the Apple Developer Portal';
        case AutomationType.AutoSyncAppleProvisioningProfilesDevices:
            return 'Sync provisioning profile devices';
        case AutomationType.AutoCreateTestRuns:
            return 'Create regression test runs';
        case AutomationType.AutoTranslateUserReviews:
            switch (ast) {
                case AppStoreType.Apple:
                    return 'Translate App Store reviews';
                case AppStoreType.Google:
                    return 'Translate Google Play reviews';
                case AppStoreType.Amazon:
                    return 'Translate Amazon Appstore reviews';
                case AppStoreType.Samsung:
                    return 'Translate Samsung Galaxy Store reviews';
                case AppStoreType.Huawei:
                    return 'Translate Huawei AppGallery reviews';
                default:
                    return '<MISSING>';
            }
        case AutomationType.AutoPostBuildInfoOnPMTickets:
            return 'Post build info to project management tickets';
        case AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest:
            return 'Create issue tickets from Triage in project management tool';
        case AutomationType.AutoAssignTriageIssue:
            return 'Assign users to issues detected in Triage';
        case AutomationType.AutoApplyLabelsToFixTickets:
            return 'Apply labels to tickets associated with fixes';
        case AutomationType.AutoAddMissingFAs:
            return 'Add missing labels or fix versions to tickets';
        case AutomationType.AutoSyncMetadataTranslations:
            return 'Sync release metadata translations';
        case AutomationType.AutoSyncMetadataFromVCS:
            return 'Sync release metadata with version control';
        case AutomationType.AutoPopulateAppClipDefaultInfo:
            return 'Auto-populate app clip default information';
        case AutomationType.AutoSelectLatestExpoUpdateGroup:
            return 'Select latest Expo update group';

        // Bucket automations
        case AutomationType.BAAutoUpdatePRWithArtifactLink:
            return 'Post installation QR code on open PRs';
        case AutomationType.BAAutoGenerateBuildTesterNotes:
            return 'Generate tester notes from the work items diff for the build';
        case AutomationType.BAAutoTriggerBuildSystemWorkflow:
            return 'Trigger build workflow';
        case AutomationType.BAAutoUploadBinaryArtifactToAppStore:
            return 'Upload build artifacts for further distribution';
        case AutomationType.BAAutoPostBuildInfoOnPMTickets:
            return 'Post build info to project management tickets';
        case AutomationType.BAAutoAssignBetaBuilds:
            return 'Assign beta testing builds to default testing groups';
        default:
            return '';
    }
};

export const displayStringForDestination = (
    platform: AppPlatform,
    appStoreType?: AppStoreType,
    name?: string,
    appName?: string,
    platformLabelOverride?: string,
) => {
    if (name) {
        return name;
    }
    const platformText = platformLabelOverride ?? displayTextForAppPlatform(platform, appStoreType);
    return appName ? `${appName} ${platformText}` : platformText;
};

export const displayTextForWorkflowRunOverviewDisplayStatus = (
    overviewDisplayStatus: WorkflowRunOverviewDisplayStatus,
) => {
    switch (overviewDisplayStatus) {
        case WorkflowRunOverviewDisplayStatus.Upcoming:
            return 'Upcoming';
        case WorkflowRunOverviewDisplayStatus.Active:
            return 'Active';
        case WorkflowRunOverviewDisplayStatus.RollingOut:
            return 'Rolling out';
        case WorkflowRunOverviewDisplayStatus.Completed:
            return 'Completed';
        case WorkflowRunOverviewDisplayStatus.Skipped:
            return 'Skipped';
    }
};

export const colorForWorkflowRunOverviewDisplayStatus = (
    overviewDisplayStatus: WorkflowRunOverviewDisplayStatus,
): string => {
    switch (overviewDisplayStatus) {
        case WorkflowRunOverviewDisplayStatus.Active:
            return runwayColors.statusYellow;
        case WorkflowRunOverviewDisplayStatus.RollingOut:
        case WorkflowRunOverviewDisplayStatus.Completed:
            return runwayColors.statusGreen;
        case WorkflowRunOverviewDisplayStatus.Upcoming:
        case WorkflowRunOverviewDisplayStatus.Skipped:
            return antGrey.grey6;
    }
};

export const displayTextForOverviewDestinationStatus = (
    status: OverviewDestinationStatus,
    platform?: AppPlatform,
    storeType?: AppStoreType,
    maybeRolloutPercentage?: number,
): string => {
    switch (status) {
        case OverviewDestinationStatus.CompletedNotReleased:
            return 'Not released';
        case OverviewDestinationStatus.CompletedFullRelease:
            return 'Released';
        case OverviewDestinationStatus.RolloutCompleted: {
            if (!platform || !storeType) {
                return 'Rollout complete';
            }

            let rolloutCompletedText: string;
            if (!getIsApplePlatform(platform) && getIsGooglePlayStore(storeType)) {
                rolloutCompletedText = 'Staged release complete';
            } else {
                rolloutCompletedText = 'Phased release complete';
            }

            return rolloutCompletedText;
        }
        case OverviewDestinationStatus.RolloutInProgress: {
            let rolloutInProgressPrefix: string;
            let rolloutInProgressFallback: string;

            if (!platform || !storeType) {
                rolloutInProgressPrefix = 'Rollout';
                rolloutInProgressFallback = 'Rollout in progress';
            } else if (!getIsApplePlatform(platform) && getIsGooglePlayStore(storeType)) {
                rolloutInProgressPrefix = 'Staged';
                rolloutInProgressFallback = 'Staged release in progress';
            } else {
                rolloutInProgressPrefix = 'Phased';
                rolloutInProgressFallback = 'Phased release in progress';
            }

            if (maybeRolloutPercentage) {
                return `${rolloutInProgressPrefix} ${maybeRolloutPercentage}%`;
            }
            return rolloutInProgressFallback;
        }
        case OverviewDestinationStatus.RolloutHalted:
            if (maybeRolloutPercentage) {
                return `Halted ${maybeRolloutPercentage}%`;
            }

            if (!platform || !storeType) {
                return 'Rollout halted';
            }

            if (!getIsApplePlatform(platform) && getIsGooglePlayStore(storeType)) {
                return 'Staged release halted';
            } else {
                return 'Phased release halted';
            }
        case OverviewDestinationStatus.Submitted:
            return 'Waiting for review';
        case OverviewDestinationStatus.InReview:
            return 'In review';
        case OverviewDestinationStatus.Approved:
            return 'Approved';
        case OverviewDestinationStatus.Rejected:
            return 'Rejected';
        case OverviewDestinationStatus.DeveloperRejected:
            return 'Developer rejected';
        case OverviewDestinationStatus.Upcoming:
            return 'Upcoming';
        case OverviewDestinationStatus.Preparing:
            return 'Preparing';
        case OverviewDestinationStatus.Skipped:
            return 'Skipped';
        case OverviewDestinationStatus.Inactive:
            return 'Inactive';
        default:
            return '';
    }
};

export const tagColorForOverviewDestinationStatus = (status: OverviewDestinationStatus): string => {
    switch (status) {
        case OverviewDestinationStatus.RolloutCompleted:
        case OverviewDestinationStatus.CompletedFullRelease:
        case OverviewDestinationStatus.RolloutInProgress:
            return runwayColors.statusGreen;
        case OverviewDestinationStatus.Submitted:
        case OverviewDestinationStatus.InReview:
        case OverviewDestinationStatus.Approved:
            return runwayColors.statusYellow;
        case OverviewDestinationStatus.RolloutHalted:
        case OverviewDestinationStatus.Rejected:
            return runwayColors.statusRed;
        case OverviewDestinationStatus.DeveloperRejected:
            return runwayColors.black;
        case OverviewDestinationStatus.Upcoming:
        case OverviewDestinationStatus.Preparing:
        case OverviewDestinationStatus.CompletedNotReleased:
        case OverviewDestinationStatus.Skipped:
            return antGrey.grey6;
        default:
            return antGrey.grey6;
    }
};

export type CITextCapitalization = 'uppercase' | 'capitalize' | 'lowercase';

export const displayCIText = (isWorkflows: boolean, capitalization: CITextCapitalization = 'capitalize'): string => {
    const baseText = isWorkflows ? 'CI' : 'Release candidate';

    if (isWorkflows) {
        return baseText;
    }

    switch (capitalization) {
        case 'uppercase':
            return baseText.toUpperCase();
        case 'capitalize':
            return baseText.charAt(0).toUpperCase() + baseText.slice(1).toLowerCase();
        case 'lowercase':
            return baseText.toLowerCase();
        default:
            return baseText;
    }
};

export const getRolloutPercentText = (
    isGooglePlayStore: boolean,
    rolloutComplete: boolean,
    userFraction: number,
): number => {
    if (isGooglePlayStore && rolloutComplete) {
        return 100; // when android release is complete show 100 because userFraction returns 0
    }
    return convertUserFraction(userFraction);
};

export const displayNameForTransactionType = (transactionType: TransactionType): string => {
    switch (transactionType) {
        case TransactionType.SubmitVersion:
            return 'Submit for store review';
        case TransactionType.ReleaseVersion:
            return 'Release version in store';
        case TransactionType.RejectAppStoreRelease:
            return 'Pull from store review';
        case TransactionType.RunWorkflow:
            return 'Trigger CI/CD workflow';
        case TransactionType.StartHotfix:
            return 'Start hotfix';
        case TransactionType.HaltRollout:
            return 'Pause rollout';
        case TransactionType.AccelerateRollout:
            return 'Release rollout to 100%';
        case TransactionType.ResumeRollout:
            return 'Resume rollout';
        case TransactionType.UpdateRegressionStatus:
            return 'Update regression testing status';
        case TransactionType.PingPendingRegressionItems:
            return 'Ping pending regression items';
        case TransactionType.PingPendingApprovalItems:
            return 'Ping pending approval items';
        case TransactionType.PopulateReleaseNotes:
            return 'Populate release notes for all locales';
        case TransactionType.ApproveMetadata:
            return 'Approve metadata';
    }
};

export const shouldShowAppImage = (isWorkflowsMigrated: boolean, useAppImage: boolean): boolean => {
    return !isWorkflowsMigrated && useAppImage;
};

export const displayEventManuallyUpdatedBy = (targetDateManuallyUpdatedAt?: string, user?: OrgUser): string => {
    let prefix = 'Manually updated';
    if (user) {
        const userName = displayUserNameShort(user);
        prefix = `Updated by ${userName}`;
    }

    if (!targetDateManuallyUpdatedAt) {
        return prefix;
    }

    return `${prefix} at ${formatDateString(targetDateManuallyUpdatedAt, 'h:mm A [on] MMM D, YYYY')}`;
};

const SCREENSHOT_TILE_TARGET_HEIGHT = 960;
const SCREENSHOT_PREVIEW_TARGET_HEIGHT = 2400;

const hostnameForUrl = (url: string): string => {
    try {
        return new URL(url).hostname;
    } catch {
        return '';
    }
};

const isGoogleImageUrl = (url: string): boolean => {
    const hostname = hostnameForUrl(url);

    return hostname === 'googleusercontent.com' || hostname.endsWith('.googleusercontent.com');
};

// App Store Connect materializes its image template at the asset's native size, so the final path
// segment is `<width>x<height>bb.<ext>`. Rewriting just that segment asks its CDN for a variant.
const appStoreConnectUrlAtHeight = (
    screenshot: Pick<Screenshot, 'url' | 'width' | 'height'>,
    targetHeight: number,
): string => {
    const { url, width, height } = screenshot;

    if (!width || !height || height <= targetHeight) {
        return url;
    }

    const lastSlashIndex = url.lastIndexOf('/');
    const nativeSize = `${width}x${height}`;
    const lastSegment = url.slice(lastSlashIndex + 1);
    if (!lastSegment.startsWith(nativeSize)) {
        return url;
    }

    const scaledSize = `${Math.round((width * targetHeight) / height)}x${targetHeight}`;

    return url.slice(0, lastSlashIndex + 1) + scaledSize + lastSegment.slice(nativeSize.length);
};

// Google Play stores a bare CDN url that serves a 512px preview by default — too small for the grid
// on a retina display and much too small for the fullscreen preview. Its CDN takes a size suffix and
// never upscales past the asset's native size, so asking for more is safe.
const googleImageUrlAtHeight = (url: string, targetHeight: number): string => {
    if (url.slice(url.lastIndexOf('/') + 1).includes('=')) {
        return url;
    }

    return `${url}=h${targetHeight}`;
};

// Samsung, Huawei and demo urls have no known resizing scheme and are returned untouched.
export const screenshotTileUrl = (screenshot: Pick<Screenshot, 'url' | 'width' | 'height' | 'previewUrl'>): string => {
    // Staged screenshots do not have a stored URL to resize
    if (screenshot.previewUrl) {
        return screenshot.previewUrl;
    }

    if (!screenshot.url) {
        return screenshot.url;
    }

    return isGoogleImageUrl(screenshot.url) ?
            googleImageUrlAtHeight(screenshot.url, SCREENSHOT_TILE_TARGET_HEIGHT)
        :   appStoreConnectUrlAtHeight(screenshot, SCREENSHOT_TILE_TARGET_HEIGHT);
};

export const screenshotPreviewUrl = (
    screenshot: Pick<Screenshot, 'url' | 'width' | 'height' | 'previewUrl'>,
): string => {
    if (screenshot.previewUrl) {
        return screenshot.previewUrl;
    }

    if (!screenshot.url) {
        return screenshot.url;
    }

    return isGoogleImageUrl(screenshot.url) ?
            googleImageUrlAtHeight(screenshot.url, SCREENSHOT_PREVIEW_TARGET_HEIGHT)
        :   screenshot.url;
};
